import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/feature/Sidebar';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';
import { mockProjects, mockDimensionAnalysisV3, mockAudienceInsights, mockContents, mockStatistics } from '../../mocks/projects';

export default function AnalysisPage() {
  const navigate = useNavigate();
  const [selectedProject] = useState(mockProjects[0]);
  const [activeTab, setActiveTab] = useState<'overview' | 'dimension' | 'audience' | 'content'>('overview');
  const [selectedSentiment, setSelectedSentiment] = useState<'all' | 'positive' | 'neutral' | 'negative'>('all');
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [selectedKeywords, setSelectedKeywords] = useState<string[]>([]);
  const [contentPlatformFilter, setContentPlatformFilter] = useState<string>('all');
  const [expandedTranscripts, setExpandedTranscripts] = useState<string[]>([]);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [selectedContentForComments, setSelectedContentForComments] = useState<string | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [showSentimentDropdown, setShowSentimentDropdown] = useState(false);
  const [showPlatformDropdown, setShowPlatformDropdown] = useState(false);
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [audiencePlatformFilter, setAudiencePlatformFilter] = useState<string>('all');
  const [showAudiencePlatformDropdown, setShowAudiencePlatformDropdown] = useState(false);
  const [selectedAudienceRole, setSelectedAudienceRole] = useState<string>('观众');
  const [overviewPlatformFilter, setOverviewPlatformFilter] = useState<string>('all');
  const [dimensionTab, setDimensionTab] = useState<'overview' | 'content' | 'design' | 'interaction' | 'activities' | 'service'>('overview');
  const [selectedPlatformForTrend, setSelectedPlatformForTrend] = useState<string>('小红书');
  const [showRelevanceModal, setShowRelevanceModal] = useState(false);
  const [selectedItemForRelevance, setSelectedItemForRelevance] = useState<{ id: string; type: 'content' | 'comment'; currentRelevance: boolean } | null>(null);
  const [relevanceNote, setRelevanceNote] = useState('');
  const [showContentDetailModal, setShowContentDetailModal] = useState(false);
  const [selectedContentDetail, setSelectedContentDetail] = useState<{
    type: 'post' | 'comment';
    post: typeof mockContents[0] | null;
    comment: typeof mockComments[0] | null;
  } | null>(null);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successToastMessage, setSuccessToastMessage] = useState('');
  
  // 原声内容明细筛选状态
  const [dimensionContentSentiment, setDimensionContentSentiment] = useState<'all' | 'positive' | 'neutral' | 'negative'>('all');
  const [dimensionContentPlatform, setDimensionContentPlatform] = useState<string>('all');
  const [dimensionContentRole, setDimensionContentRole] = useState<string>('all');
  const [dimensionContentType, setDimensionContentType] = useState<'all' | 'post' | 'comment'>('all');
  const [dimensionContentStartDate, setDimensionContentStartDate] = useState('');
  const [dimensionContentEndDate, setDimensionContentEndDate] = useState('');
  const [showDimensionSentimentDropdown, setShowDimensionSentimentDropdown] = useState(false);
  const [showDimensionPlatformDropdown, setShowDimensionPlatformDropdown] = useState(false);
  const [showDimensionRoleDropdown, setShowDimensionRoleDropdown] = useState(false);
  const [showDimensionTypeDropdown, setShowDimensionTypeDropdown] = useState(false);
  const [showDimensionTimeDropdown, setShowDimensionTimeDropdown] = useState(false);
  
  const stats = mockStatistics;
  
  // 过滤内容
  const filteredContents = mockContents.filter(content => {
    const matchesSentiment = selectedSentiment === 'all' || content.sentiment === selectedSentiment;
    const matchesPlatform = selectedPlatforms.length === 0 || selectedPlatforms.includes(content.platform);
    const matchesContentPlatform = contentPlatformFilter === 'all' || content.platform === contentPlatformFilter;
    const matchesRole = selectedRoles.length === 0 || selectedRoles.includes(content.authorType);
    
    // 时间筛选逻辑
    let matchesTime = true;
    if (customStartDate && customEndDate) {
      const contentDate = new Date(content.publishTime);
      const startDate = new Date(customStartDate);
      const endDate = new Date(customEndDate);
      endDate.setHours(23, 59, 59, 999);
      matchesTime = contentDate >= startDate && contentDate <= endDate;
    }
    
    return matchesSentiment && matchesPlatform && matchesContentPlatform && matchesRole && matchesTime;
  });

  // 计算人群画像数据（根据平台筛选）
  const getAudienceDataByPlatform = () => {
    const filteredByPlatform = audiencePlatformFilter === 'all' 
      ? mockContents 
      : mockContents.filter(c => c.platform === audiencePlatformFilter);
    
    const roleStats: Record<string, { count: number; positive: number; neutral: number; negative: number; avgEngagement: number }> = {};
    
    filteredByPlatform.forEach(content => {
      if (!roleStats[content.authorType]) {
        roleStats[content.authorType] = { count: 0, positive: 0, neutral: 0, negative: 0, avgEngagement: 0 };
      }
      roleStats[content.authorType].count++;
      if (content.sentiment === 'positive') roleStats[content.authorType].positive++;
      if (content.sentiment === 'neutral') roleStats[content.authorType].neutral++;
      if (content.sentiment === 'negative') roleStats[content.authorType].negative++;
      roleStats[content.authorType].avgEngagement += content.likes + content.comments;
    });
    
    const totalCount = filteredByPlatform.length;
    
    return Object.entries(roleStats).map(([type, data]) => ({
      type,
      count: data.count,
      percentage: ((data.count / totalCount) * 100).toFixed(1),
      positive: ((data.positive / data.count) * 100).toFixed(1),
      neutral: ((data.neutral / data.count) * 100).toFixed(1),
      negative: ((data.negative / data.count) * 100).toFixed(1),
      avgEngagement: Math.round(data.avgEngagement / data.count),
    }));
  };

  const audienceData = getAudienceDataByPlatform();

  // 计算各角色在各平台的分布
  const getRolePlatformDistribution = () => {
    const roles = ['观众', 'KOL', '官方', '专家'];
    const platforms = selectedProject.platforms;
    
    const distribution: Record<string, Record<string, number>> = {};
    
    roles.forEach(role => {
      distribution[role] = {};
      platforms.forEach(platform => {
        distribution[role][platform] = 0;
      });
    });
    
    mockContents.forEach(content => {
      if (distribution[content.authorType] && distribution[content.authorType][content.platform] !== undefined) {
        distribution[content.authorType][content.platform]++;
      }
    });
    
    return { roles, platforms, distribution };
  };

  const rolePlatformData = getRolePlatformDistribution();
  
  // 切换平台筛选
  const togglePlatform = (platform: string) => {
    setSelectedPlatforms(prev => 
      prev.includes(platform) 
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };
  
  // 切换关键词筛选
  const toggleKeyword = (keyword: string) => {
    setSelectedKeywords(prev => 
      prev.includes(keyword) 
        ? prev.filter(k => k !== keyword)
        : [...prev, keyword]
    );
  };

  // 切换角色筛选
  const toggleRole = (role: string) => {
    setSelectedRoles(prev => 
      prev.includes(role) 
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  // 打开评论弹窗
  const openCommentsModal = (contentId: string) => {
    setSelectedContentForComments(contentId);
    setShowCommentsModal(true);
  };

  // 关闭评论弹窗
  const closeCommentsModal = () => {
    setShowCommentsModal(false);
    setSelectedContentForComments(null);
  };

  // 切换字幕展开
  const toggleTranscript = (contentId: string) => {
    setExpandedTranscripts(prev =>
      prev.includes(contentId)
        ? prev.filter(id => id !== contentId)
        : [...prev, contentId]
    );
  };
  
  // 刷新数据
  const handleRefreshData = () => {
    console.log('刷新数据');
    // 这里添加刷新数据的逻辑
  };

  // 打开内容详情弹窗
  const openContentDetailModal = (type: 'post' | 'comment', post: typeof mockContents[0] | null, comment: typeof mockComments[0] | null = null) => {
    setSelectedContentDetail({ type, post, comment });
    setShowContentDetailModal(true);
  };

  // 关闭内容详情弹窗
  const closeContentDetailModal = () => {
    setShowContentDetailModal(false);
    setSelectedContentDetail(null);
  };

  // 获取平台颜色样式
  const getPlatformStyle = (platform: string, isSelected: boolean) => {
    const styles = {
      '小红书': {
        selected: 'bg-[#FF2442] text-white shadow-sm',
        unselected: 'bg-[#FF2442]/10 text-[#FF2442] hover:bg-[#FF2442]/20 border border-[#FF2442]/30'
      },
      '微博': {
        selected: 'bg-[#FBE89F] text-gray-900 shadow-sm',
        unselected: 'bg-[#FBE89F]/10 text-[#FBE89F] hover:bg-[#FBE89F]/20 border border-[#FBE89F]/30'
      },
      '抖音': {
        selected: 'bg-[#000000] text-white shadow-sm',
        unselected: 'bg-[#000000]/10 text-[#000000] hover:bg-[#000000]/20 border border-[#000000]/30'
      },
      '视频号': {
        selected: 'bg-[#EAA054] text-white shadow-sm',
        unselected: 'bg-[#EAA054]/10 text-[#EAA054] hover:bg-[#EAA054]/20 border border-[#EAA054]/30'
      }
    };
    return styles[platform as keyof typeof styles]?.[isSelected ? 'selected' : 'unselected'] || '';
  };

  // 获取平台标签颜色
  const getPlatformBadgeStyle = (platform: string) => {
    const styles = {
      '小红书': 'bg-[#FF2442]/10 text-[#FF2442]',
      '微博': 'bg-[#FBE89F]/10 text-[#FBE89F]',
      '抖音': 'bg-[#000000]/10 text-[#000000]',
      '视频号': 'bg-[#EAA054]/10 text-[#EAA054]'
    };
    return styles[platform as keyof typeof styles] || 'bg-gray-100 text-gray-700';
  };
  
  // 获取已选筛选数量
  const getActiveFilterCount = () => {
    return selectedPlatforms.length + selectedKeywords.length;
  };

  // 清空筛选
  const clearFilters = () => {
    setSelectedPlatforms([]);
    setSelectedKeywords([]);
  };

  // 获取情感标签样式
  const getSentimentBadgeStyle = (sentiment: 'positive' | 'neutral' | 'negative') => {
    const styles = {
      positive: 'bg-green-100 text-green-700',
      neutral: 'bg-gray-100 text-gray-700',
      negative: 'bg-red-100 text-red-700'
    };
    return styles[sentiment];
  };

  // 获取情感标签文本
  const getSentimentText = (sentiment: 'positive' | 'neutral' | 'negative') => {
    const texts = {
      positive: '正面',
      neutral: '中性',
      negative: '负面'
    };
    return texts[sentiment];
  };

  // 模拟评论数据（带情感判断）
  const mockComments = [
    { user: '艺术迷小李', time: '2小时前', text: '太赞了！我也想去看看', likes: 45, sentiment: 'positive' as const, relevanceScore: 88, sentimentScore: 95, influenceScore: 72 },
    { user: '文化爱好者', time: '5小时前', text: '灯光设计确实很棒，上周去过，印象深刻', likes: 32, sentiment: 'positive' as const, relevanceScore: 92, sentimentScore: 90, influenceScore: 68 },
    { user: '博物馆控', time: '8小时前', text: '请问周末人多吗？想找个人少的时候去', likes: 28, sentiment: 'neutral' as const, relevanceScore: 75, sentimentScore: 60, influenceScore: 55 },
    { user: '摄影师阿明', time: '1天前', text: '拍照效果很好，光线柔和不刺眼', likes: 56, sentiment: 'positive' as const, relevanceScore: 85, sentimentScore: 88, influenceScore: 78 },
    { user: '历史老师王', time: '1天前', text: '很有教育意义，适合带学生来参观学习', likes: 41, sentiment: 'positive' as const, relevanceScore: 90, sentimentScore: 86, influenceScore: 70 },
    { user: '游客张三', time: '2天前', text: '展览内容还可以，但是人太多了', likes: 23, sentiment: 'neutral' as const, relevanceScore: 78, sentimentScore: 62, influenceScore: 52 },
    { user: '艺术学生', time: '2天前', text: '从学术角度看非常有价值，推荐！', likes: 38, sentiment: 'positive' as const, relevanceScore: 94, sentimentScore: 92, influenceScore: 65 },
    { user: '普通观众', time: '3天前', text: '票价有点贵，但是展览质量确实不错', likes: 19, sentiment: 'neutral' as const, relevanceScore: 80, sentimentScore: 68, influenceScore: 48 },
  ];

  // 根据维度获取主题相关的高亮句子数据
  const getDimensionHighlightedContent = (dimension: string) => {
    const contentByDimension: Record<string, Array<{
      id: string;
      type: 'post' | 'comment';
      author: string;
      authorType: string;
      platform: string;
      time: string;
      originalText: string;
      highlights: Array<{
        text: string;
        sentiment: 'positive' | 'neutral' | 'negative';
        startIndex: number;
        endIndex: number;
      }>;
    }>> = {
      content: [
        {
          id: 'c1',
          type: 'post',
          author: '艺术爱好者小王',
          authorType: '观众',
          platform: '小红书',
          time: '2024-02-15 14:23',
          originalText: '敦煌艺术特展真的太震撼了！展品内容非常丰富，涵盖了壁画、雕塑、文献等多种形式。每一件展品都讲述着千年的故事，让人仿佛穿越回了古代丝绸之路。不过部分展品的说明文字太少了，希望能增加更多背景介绍。',
          highlights: [
            { text: '展品内容非常丰富，涵盖了壁画、雕塑、文献等多种形式', sentiment: 'positive', startIndex: 14, endIndex: 42 },
            { text: '每一件展品都讲述着千年的故事', sentiment: 'positive', startIndex: 43, endIndex: 58 },
            { text: '部分展品的说明文字太少了，希望能增加更多背景介绍', sentiment: 'negative', startIndex: 75, endIndex: 98 },
          ]
        },
        {
          id: 'c2',
          type: 'comment',
          author: '历史老师王',
          authorType: '专家',
          platform: '微博',
          time: '2024-02-15 11:30',
          originalText: '从教育角度来看，这次展览的内容编排很有逻辑性，适合作为历史教学的补充材料。展品的历史价值很高，但是缺少互动体验环节。',
          highlights: [
            { text: '内容编排很有逻辑性，适合作为历史教学的补充材料', sentiment: 'positive', startIndex: 12, endIndex: 36 },
            { text: '展品的历史价值很高', sentiment: 'positive', startIndex: 37, endIndex: 46 },
            { text: '缺少互动体验环节', sentiment: 'negative', startIndex: 50, endIndex: 58 },
          ]
        },
        {
          id: 'c3',
          type: 'post',
          author: '文化探索者',
          authorType: 'KOL',
          platform: '抖音',
          time: '2024-02-14 16:45',
          originalText: '打卡敦煌艺术特展！内容深度很够，能感受到策展团队的用心。唯一遗憾是部分珍贵文物只有复制品，原件没有展出。整体内容质量还是很高的。',
          highlights: [
            { text: '内容深度很够，能感受到策展团队的用心', sentiment: 'positive', startIndex: 9, endIndex: 28 },
            { text: '部分珍贵文物只有复制品，原件没有展出', sentiment: 'negative', startIndex: 33, endIndex: 51 },
            { text: '整体内容质量还是很高的', sentiment: 'positive', startIndex: 52, endIndex: 63 },
          ]
        },
        {
          id: 'c4',
          type: 'comment',
          author: '博物馆控',
          authorType: '观众',
          platform: '小红书',
          time: '2024-02-13 09:15',
          originalText: '展览内容涵盖面广，从北魏到元代都有涉及。不过感觉内容有点多，一次看不完，建议分两次来。',
          highlights: [
            { text: '内容涵盖面广，从北魏到元代都有涉及', sentiment: 'positive', startIndex: 2, endIndex: 20 },
            { text: '内容有点多，一次看不完', sentiment: 'neutral', startIndex: 25, endIndex: 36 },
          ]
        },
      ],
      design: [
        {
          id: 'd1',
          type: 'post',
          author: '设计师小美',
          authorType: 'KOL',
          platform: '小红书',
          time: '2024-02-15 13:20',
          originalText: '作为设计师，必须夸一下这次展览的空间设计！灯光设计非常专业，完美还原了洞窟的神秘氛围。展厅的动线设计也很合理，不会让人迷路。但是部分区域的色温偏冷，影响了壁画的观感。',
          highlights: [
            { text: '灯光设计非常专业，完美还原了洞窟的神秘氛围', sentiment: 'positive', startIndex: 18, endIndex: 40 },
            { text: '展厅的动线设计也很合理，不会让人迷路', sentiment: 'positive', startIndex: 41, endIndex: 58 },
            { text: '部分区域的色温偏冷，影响了壁画的观感', sentiment: 'negative', startIndex: 62, endIndex: 80 },
          ]
        },
        {
          id: 'd2',
          type: 'comment',
          author: '摄影师阿明',
          authorType: '观众',
          platform: '微博',
          time: '2024-02-15 10:45',
          originalText: '展览的视觉设计太棒了！每个展区的主题色彩搭配都很和谐，拍照效果超好。指示牌的设计风格统一，很有敦煌特色。',
          highlights: [
            { text: '视觉设计太棒了', sentiment: 'positive', startIndex: 3, endIndex: 10 },
            { text: '主题色彩搭配都很和谐，拍照效果超好', sentiment: 'positive', startIndex: 18, endIndex: 35 },
            { text: '指示牌的设计风格统一，很有敦煌特色', sentiment: 'positive', startIndex: 36, endIndex: 52 },
          ]
        },
        {
          id: 'd3',
          type: 'post',
          author: '艺术学生',
          authorType: '观众',
          platform: '视频号',
          time: '2024-02-14 18:30',
          originalText: '从专业角度分析，展览的整体设计水准很高。沉浸式的场景设计让人身临其境，但是有些多媒体装置的分辨率不够高，影响了沉浸感。展板的排版设计简洁大方。',
          highlights: [
            { text: '整体设计水准很高', sentiment: 'positive', startIndex: 10, endIndex: 18 },
            { text: '沉浸式的场景设计让人身临其境', sentiment: 'positive', startIndex: 19, endIndex: 33 },
            { text: '多媒体装置的分辨率不够高，影响了沉浸感', sentiment: 'negative', startIndex: 38, endIndex: 56 },
            { text: '展板的排版设计简洁大方', sentiment: 'positive', startIndex: 57, endIndex: 68 },
          ]
        },
        {
          id: 'd4',
          type: 'comment',
          author: '普通游客',
          authorType: '观众',
          platform: '抖音',
          time: '2024-02-13 15:20',
          originalText: '灯光效果很美，但是有些地方太暗了看不清展品说明。整体装修风格很有古典韵味。',
          highlights: [
            { text: '灯光效果很美', sentiment: 'positive', startIndex: 0, endIndex: 6 },
            { text: '有些地方太暗了看不清展品说明', sentiment: 'negative', startIndex: 9, endIndex: 23 },
            { text: '整体装修风格很有古典韵味', sentiment: 'positive', startIndex: 24, endIndex: 36 },
          ]
        },
      ],
      interaction: [
        {
          id: 'i1',
          type: 'post',
          author: '亲子博主',
          authorType: 'KOL',
          platform: '小红书',
          time: '2024-02-15 12:30',
          originalText: '带娃来看展！互动体验区太赞了，孩子可以亲手体验壁画临摹，寓教于乐。AR互动装置也很有趣，扫一扫就能看到壁画动起来。不过排队时间有点长，建议错峰参观。',
          highlights: [
            { text: '互动体验区太赞了，孩子可以亲手体验壁画临摹，寓教于乐', sentiment: 'positive', startIndex: 6, endIndex: 32 },
            { text: 'AR互动装置也很有趣，扫一扫就能看到壁画动起来', sentiment: 'positive', startIndex: 33, endIndex: 55 },
            { text: '排队时间有点长', sentiment: 'negative', startIndex: 59, endIndex: 66 },
          ]
        },
        {
          id: 'i2',
          type: 'comment',
          author: '科技爱好者',
          authorType: '观众',
          platform: '微博',
          time: '2024-02-15 08:45',
          originalText: '数字互动体验做得很用心，VR洞窟探索太震撼了！触摸屏的响应速度也很快。希望能增加更多互动环节。',
          highlights: [
            { text: '数字互动体验做得很用心', sentiment: 'positive', startIndex: 0, endIndex: 11 },
            { text: 'VR洞窟探索太震撼了', sentiment: 'positive', startIndex: 12, endIndex: 22 },
            { text: '触摸屏的响应速度也很快', sentiment: 'positive', startIndex: 23, endIndex: 34 },
            { text: '希望能增加更多互动环节', sentiment: 'neutral', startIndex: 35, endIndex: 46 },
          ]
        },
        {
          id: 'i3',
          type: 'post',
          author: '文化爱好者',
          authorType: '观众',
          platform: '抖音',
          time: '2024-02-14 19:15',
          originalText: '互动体验一般般，感觉互动设备有点少，大部分时间都在排队。不过工作人员的讲解互动很专业，解答了很多疑问。',
          highlights: [
            { text: '互动体验一般般', sentiment: 'neutral', startIndex: 0, endIndex: 7 },
            { text: '互动设备有点少，大部分时间都在排队', sentiment: 'negative', startIndex: 10, endIndex: 26 },
            { text: '工作人员的讲解互动很专业，解答了很多疑问', sentiment: 'positive', startIndex: 30, endIndex: 50 },
          ]
        },
      ],
      activities: [
        {
          id: 'a1',
          type: 'post',
          author: '手工达人',
          authorType: 'KOL',
          platform: '小红书',
          time: '2024-02-15 11:20',
          originalText: '参加了敦煌壁画临摹工作坊，活动组织得很好！老师讲解细致，材料也很齐全。活动时长2小时刚刚好，不会太累。强烈推荐大家报名参加！',
          highlights: [
            { text: '活动组织得很好', sentiment: 'positive', startIndex: 14, endIndex: 21 },
            { text: '老师讲解细致，材料也很齐全', sentiment: 'positive', startIndex: 22, endIndex: 35 },
            { text: '活动时长2小时刚刚好，不会太累', sentiment: 'positive', startIndex: 36, endIndex: 50 },
          ]
        },
        {
          id: 'a2',
          type: 'comment',
          author: '周末玩家',
          authorType: '观众',
          platform: '微博',
          time: '2024-02-14 20:30',
          originalText: '周末的专家讲座很精彩，学到了很多敦煌历史知识。但是活动名额太少了，抢了好几次才抢到。希望能增加场次。',
          highlights: [
            { text: '专家讲座很精彩，学到了很多敦煌历史知识', sentiment: 'positive', startIndex: 3, endIndex: 22 },
            { text: '活动名额太少了，抢了好几次才抢到', sentiment: 'negative', startIndex: 26, endIndex: 42 },
            { text: '希望能增加场次', sentiment: 'neutral', startIndex: 43, endIndex: 50 },
          ]
        },
        {
          id: 'a3',
          type: 'post',
          author: '文艺青年',
          authorType: '观众',
          platform: '视频号',
          time: '2024-02-13 21:45',
          originalText: '夜场活动氛围超棒！灯光秀配合音乐，整个展厅都活了起来。活动流程安排合理，体验感很好。就是票价有点贵。',
          highlights: [
            { text: '夜场活动氛围超棒', sentiment: 'positive', startIndex: 0, endIndex: 8 },
            { text: '灯光秀配合音乐，整个展厅都活了起来', sentiment: 'positive', startIndex: 9, endIndex: 26 },
            { text: '活动流程安排合理，体验感很好', sentiment: 'positive', startIndex: 27, endIndex: 41 },
            { text: '票价有点贵', sentiment: 'negative', startIndex: 44, endIndex: 49 },
          ]
        },
      ],
      service: [
        {
          id: 's1',
          type: 'post',
          author: '旅游达人',
          authorType: 'KOL',
          platform: '小红书',
          time: '2024-02-15 13:45',
          originalText: '服务体验总体不错！工作人员态度很好，问路都会耐心指引。讲解器音质清晰，内容丰富。但是洗手间数量不够，高峰期要排很久的队。存包处也需要排队。',
          highlights: [
            { text: '工作人员态度很好，问路都会耐心指引', sentiment: 'positive', startIndex: 9, endIndex: 26 },
            { text: '讲解器音质清晰，内容丰富', sentiment: 'positive', startIndex: 27, endIndex: 39 },
            { text: '洗手间数量不够，高峰期要排很久的队', sentiment: 'negative', startIndex: 43, endIndex: 60 },
            { text: '存包处也需要排队', sentiment: 'negative', startIndex: 61, endIndex: 69 },
          ]
        },
        {
          id: 's2',
          type: 'comment',
          author: '带娃妈妈',
          authorType: '观众',
          platform: '微博',
          time: '2024-02-15 10:20',
          originalText: '带孩子来很方便，有母婴室和儿童推车租借服务。餐饮区的价格合理，味道也不错。就是停车位太难找了。',
          highlights: [
            { text: '有母婴室和儿童推车租借服务', sentiment: 'positive', startIndex: 8, endIndex: 21 },
            { text: '餐饮区的价格合理，味道也不错', sentiment: 'positive', startIndex: 22, endIndex: 36 },
            { text: '停车位太难找了', sentiment: 'negative', startIndex: 39, endIndex: 46 },
          ]
        },
        {
          id: 's3',
          type: 'post',
          author: '外地游客',
          authorType: '观众',
          platform: '抖音',
          time: '2024-02-14 17:30',
          originalText: '购票流程很顺畅，线上预约很方便。入场检票速度快，没有排太久。纪念品商店的商品种类丰富，但是价格偏高。整体服务水平还是很专业的。',
          highlights: [
            { text: '购票流程很顺畅，线上预约很方便', sentiment: 'positive', startIndex: 0, endIndex: 15 },
            { text: '入场检票速度快，没有排太久', sentiment: 'positive', startIndex: 16, endIndex: 29 },
            { text: '纪念品商店的商品种类丰富', sentiment: 'positive', startIndex: 30, endIndex: 42 },
            { text: '价格偏高', sentiment: 'negative', startIndex: 46, endIndex: 50 },
            { text: '整体服务水平还是很专业的', sentiment: 'positive', startIndex: 51, endIndex: 63 },
          ]
        },
        {
          id: 's4',
          type: 'comment',
          author: '老年观众',
          authorType: '观众',
          platform: '视频号',
          time: '2024-02-13 14:15',
          originalText: '无障碍设施做得很好，轮椅通道畅通。志愿者服务很贴心，主动帮忙。休息区座位充足，累了可以随时休息。',
          highlights: [
            { text: '无障碍设施做得很好，轮椅通道畅通', sentiment: 'positive', startIndex: 0, endIndex: 16 },
            { text: '志愿者服务很贴心，主动帮忙', sentiment: 'positive', startIndex: 17, endIndex: 30 },
            { text: '休息区座位充足，累了可以随时休息', sentiment: 'positive', startIndex: 31, endIndex: 47 },
          ]
        },
      ],
    };
    
    return contentByDimension[dimension] || [];
  };

  // 渲染高亮文本
  const renderHighlightedText = (originalText: string, highlights: Array<{ text: string; sentiment: 'positive' | 'neutral' | 'negative' }>) => {
    if (!highlights || highlights.length === 0) {
      return <span className="text-gray-600">{originalText}</span>;
    }

    const parts: Array<{ text: string; sentiment?: 'positive' | 'neutral' | 'negative'; isHighlight: boolean }> = [];
    let lastIndex = 0;

    // 按照在原文中出现的顺序排序高亮内容
    const sortedHighlights = [...highlights].sort((a, b) => {
      const indexA = originalText.indexOf(a.text);
      const indexB = originalText.indexOf(b.text);
      return indexA - indexB;
    });

    sortedHighlights.forEach((highlight) => {
      const startIndex = originalText.indexOf(highlight.text, lastIndex);
      if (startIndex === -1) return;

      // 添加高亮前的普通文本
      if (startIndex > lastIndex) {
        parts.push({
          text: originalText.slice(lastIndex, startIndex),
          isHighlight: false,
        });
      }

      // 添加高亮文本
      parts.push({
        text: highlight.text,
        sentiment: highlight.sentiment,
        isHighlight: true,
      });

      lastIndex = startIndex + highlight.text.length;
    });

    // 添加最后剩余的普通文本
    if (lastIndex < originalText.length) {
      parts.push({
        text: originalText.slice(lastIndex),
        isHighlight: false,
      });
    }

    return (
      <span>
        {parts.map((part, idx) => {
          if (!part.isHighlight) {
            return <span key={idx} className="text-gray-600">{part.text}</span>;
          }

          const bgColor = part.sentiment === 'positive' 
            ? 'bg-green-100 border-b-2 border-green-400' 
            : part.sentiment === 'negative' 
              ? 'bg-red-100 border-b-2 border-red-400' 
              : 'bg-gray-200 border-b-2 border-gray-400';
          
          const textColor = part.sentiment === 'positive'
            ? 'text-green-800'
            : part.sentiment === 'negative'
              ? 'text-red-800'
              : 'text-gray-800';

          return (
            <span 
              key={idx} 
              className={`${bgColor} ${textColor} px-1 py-0.5 rounded font-medium`}
              title={`${part.sentiment === 'positive' ? '正面' : part.sentiment === 'negative' ? '负面' : '中性'}观点`}
            >
              {part.text}
            </span>
          );
        })}
      </span>
    );
  };

  // 获取高亮内容的情感统计
  const getHighlightStats = (contents: ReturnType<typeof getDimensionHighlightedContent>) => {
    let positive = 0;
    let neutral = 0;
    let negative = 0;

    contents.forEach(content => {
      content.highlights.forEach(h => {
        if (h.sentiment === 'positive') positive++;
        else if (h.sentiment === 'neutral') neutral++;
        else negative++;
      });
    });

    return { positive, neutral, negative, total: positive + neutral + negative };
  };

  // 打开相关性标记弹窗
  const openRelevanceModal = (id: string, type: 'content' | 'comment', currentRelevance: boolean) => {
    setSelectedItemForRelevance({ id, type, currentRelevance });
    setRelevanceNote('');
    setShowRelevanceModal(true);
  };

  // 关闭相关性标记弹窗
  const closeRelevanceModal = () => {
    setShowRelevanceModal(false);
    setSelectedItemForRelevance(null);
    setRelevanceNote('');
  };

  // 提交相关性标记
  const submitRelevanceMark = (isRelevant: boolean) => {
    if (!selectedItemForRelevance) return;
    
    // 这里添加提交逻辑，例如调用API保存标记
    console.log('标记相关性:', {
      id: selectedItemForRelevance.id,
      type: selectedItemForRelevance.type,
      isRelevant,
      note: relevanceNote,
      timestamp: new Date().toISOString()
    });
    
    // 显示成功提示（使用自定义提示替代alert）
    setShowSuccessToast(true);
    setSuccessToastMessage(`已标记为${isRelevant ? '相关' : '不相关'}`);
    setTimeout(() => setShowSuccessToast(false), 2000);
    
    closeRelevanceModal();
  };
  
  // 应用自定义时间筛选
  const applyCustomDateFilter = () => {
    if (customStartDate && customEndDate) {
      setShowCustomDatePicker(false);
    }
  };

  // 重置自定义时间筛选
  const resetCustomDateFilter = () => {
    setCustomStartDate('');
    setCustomEndDate('');
    setShowCustomDatePicker(false);
  };

  // 获取平台互动数据配置
  const getPlatformInteractionConfig = (platform: string) => {
    const configs = {
      '微博': [
        { icon: 'ri-thumb-up-line', label: '点赞', key: 'likes' },
        { icon: 'ri-chat-3-line', label: '评论', key: 'comments' },
        { icon: 'ri-share-line', label: '分享', key: 'shares' }
      ],
      '小红书': [
        { icon: 'ri-heart-line', label: '喜爱', key: 'likes' },
        { icon: 'ri-chat-3-line', label: '评论', key: 'comments' },
        { icon: 'ri-bookmark-line', label: '收藏', key: 'collects' }
      ],
      '抖音': [
        { icon: 'ri-heart-line', label: '喜爱', key: 'likes' },
        { icon: 'ri-chat-3-line', label: '评论', key: 'comments' },
        { icon: 'ri-bookmark-line', label: '收藏', key: 'collects' },
        { icon: 'ri-share-line', label: '分享', key: 'shares' }
      ],
      '视频号': [
        { icon: 'ri-thumb-up-line', label: '点赞', key: 'likes' },
        { icon: 'ri-chat-3-line', label: '评论', key: 'comments' },
        { icon: 'ri-heart-line', label: '喜爱', key: 'favorites' },
        { icon: 'ri-share-line', label: '分享', key: 'shares' }
      ]
    };
    return configs[platform as keyof typeof configs] || [];
  };

  // 获取角色词云数据
  const getRoleWordCloud = (role: string) => {
    const wordClouds: Record<string, Array<{ word: string; size: number; color: string }>> = {
      '观众': [
        { word: '震撼', size: 48, color: '#4A8AE9' },
        { word: '精美', size: 42, color: '#5A9EED' },
        { word: '值得', size: 38, color: '#6AAEF1' },
        { word: '推荐', size: 36, color: '#7ABEF5' },
        { word: '氛围', size: 34, color: '#8ACEF9' },
        { word: '灯光', size: 32, color: '#9ADEFD' },
        { word: '布景', size: 30, color: '#AAEEFF' },
        { word: '壮观', size: 28, color: '#6A9EED' },
        { word: '感动', size: 26, color: '#7ABEF5' },
        { word: '美丽', size: 24, color: '#8ACEF9' },
        { word: '精致', size: 22, color: '#9ADEFD' },
        { word: '喜欢', size: 20, color: '#AAEEFF' },
      ],
      'KOL': [
        { word: '专业', size: 46, color: '#4A8AE9' },
        { word: '策展', size: 40, color: '#5A9EED' },
        { word: '设计', size: 38, color: '#6AAEF1' },
        { word: '艺术', size: 36, color: '#7ABEF5' },
        { word: '创意', size: 34, color: '#8ACEF9' },
        { word: '视觉', size: 32, color: '#9ADEFD' },
        { word: '体验', size: 30, color: '#AAEEFF' },
        { word: '品质', size: 28, color: '#6A9EED' },
        { word: '细节', size: 26, color: '#7ABEF5' },
        { word: '呈现', size: 24, color: '#8ACEF9' },
        { word: '水平', size: 22, color: '#9ADEFD' },
        { word: '效果', size: 20, color: '#AAEEFF' },
      ],
      '官方': [
        { word: '展览', size: 44, color: '#4A8AE9' },
        { word: '文化', size: 42, color: '#5A9EED' },
        { word: '艺术', size: 40, color: '#6AAEF1' },
        { word: '敦煌', size: 38, color: '#7ABEF5' },
        { word: '传承', size: 36, color: '#8ACEF9' },
        { word: '历史', size: 34, color: '#9ADEFD' },
        { word: '精品', size: 32, color: '#AAEEFF' },
        { word: '珍贵', size: 30, color: '#6A9EED' },
        { word: '保护', size: 28, color: '#7ABEF5' },
        { word: '弘扬', size: 26, color: '#8ACEF9' },
        { word: '传播', size: 24, color: '#9ADEFD' },
        { word: '价值', size: 22, color: '#AAEEFF' },
      ],
      '专家': [
        { word: '学术', size: 46, color: '#4A8AE9' },
        { word: '研究', size: 44, color: '#5A9EED' },
        { word: '价值', size: 40, color: '#6AAEF1' },
        { word: '历史', size: 38, color: '#7ABEF5' },
        { word: '文物', size: 36, color: '#8ACEF9' },
        { word: '考证', size: 34, color: '#9ADEFD' },
        { word: '分析', size: 32, color: '#AAEEFF' },
        { word: '意义', size: 30, color: '#6A9EED' },
        { word: '背景', size: 28, color: '#7ABEF5' },
        { word: '深度', size: 26, color: '#8ACEF9' },
        { word: '解读', size: 24, color: '#9ADEFD' },
        { word: '专业', size: 22, color: '#AAEEFF' },
      ],
    };
    return wordClouds[role] || [];
  };

  // 根据平台筛选获取概览数据
  const getOverviewDataByPlatform = () => {
    const filteredByPlatform = overviewPlatformFilter === 'all' 
      ? mockContents 
      : mockContents.filter(c => c.platform === overviewPlatformFilter);
    
    const totalVolume = filteredByPlatform.length;
    const totalInteraction = filteredByPlatform.reduce((sum, item) => sum + item.likes + item.comments, 0);
    const positiveCount = filteredByPlatform.filter(c => c.sentiment === 'positive').length;
    const positiveRate = totalVolume > 0 ? ((positiveCount / totalVolume) * 100).toFixed(1) : '0.0';
    
    return {
      totalVolume,
      totalInteraction,
      positiveRate
    };
  };

  const overviewStats = getOverviewDataByPlatform();

  // 获取观点类型样式
  const getInsightStyle = (type: 'positive' | 'negative') => {
    const styles = {
      positive: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        icon: 'ri-thumb-up-line',
        iconColor: 'text-green-600',
        textColor: 'text-green-900',
        countBg: 'bg-green-100',
        countText: 'text-green-700',
      },
      negative: {
        bg: 'bg-red-50',
        border: 'border-red-200',
        icon: 'ri-thumb-down-line',
        iconColor: 'text-red-600',
        textColor: 'text-red-900',
        countBg: 'bg-red-100',
        countText: 'text-red-700',
      },
    };
    return styles[type];
  };

  // 获取维度图标
  const getDimensionIcon = (key: string) => {
    const icons: Record<string, string> = {
      content: 'ri-file-text-line',
      design: 'ri-palette-line',
      interaction: 'ri-hand-heart-line',
      activities: 'ri-calendar-event-line',
      service: 'ri-customer-service-line',
    };
    return icons[key] || 'ri-checkbox-circle-line';
  };

  // 获取维度名称
  const getDimensionName = (key: string) => {
    const names: Record<string, string> = {
      content: '内容',
      design: '设计',
      interaction: '互动',
      activities: '活动',
      service: '服务',
    };
    return names[key] || key;
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      
      <main className="flex-1 ml-64">
        <div className="p-8">
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate('/config')}
              className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-gray-700 text-lg"></i>
            </button>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900">{selectedProject.name}</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {selectedProject.startDate} 至 {selectedProject.endDate}
              </p>
            </div>
            <Button variant="outline" onClick={() => setShowConfigModal(true)}>
              <i className="ri-settings-3-line mr-2"></i>
              项目配置
            </Button>
            <Button variant="outline" onClick={handleRefreshData}>
              <i className="ri-refresh-line mr-2"></i>
              刷新数据
            </Button>
            <Button>
              <i className="ri-download-line mr-2"></i>
              导出报告
            </Button>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 mb-6">
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-6 py-4 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'overview'
                    ? 'text-[#4A8AE9] border-b-2 border-[#4A8AE9]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                监控概览
              </button>
              <button
                onClick={() => setActiveTab('dimension')}
                className={`px-6 py-4 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'dimension'
                    ? 'text-[#4A8AE9] border-b-2 border-[#4A8AE9]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                多维评价分析
              </button>
              <button
                onClick={() => setActiveTab('audience')}
                className={`px-6 py-4 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'audience'
                    ? 'text-[#4A8AE9] border-b-2 border-[#4A8AE9]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                人群画像洞察
              </button>
              <button
                onClick={() => setActiveTab('content')}
                className={`px-6 py-4 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'content'
                    ? 'text-[#4A8AE9] border-b-2 border-[#4A8AE9]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                原声内容
              </button>
            </div>
          </div>
          
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* 平台筛选 */}
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 bg-white border border-gray-300 rounded-lg p-1 flex-shrink-0">
                  <button
                    onClick={() => setOverviewPlatformFilter('all')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex-shrink-0 flex items-center gap-1.5 ${
                      overviewPlatformFilter === 'all'
                        ? 'bg-[#4A8AE9] text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <i className="ri-apps-line text-base"></i>
                    <span>全部平台</span>
                  </button>
                  {selectedProject.platforms.map((platform, idx) => (
                    <button
                      key={idx}
                      onClick={() => setOverviewPlatformFilter(platform)}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex-shrink-0 flex items-center gap-1.5 ${
                        overviewPlatformFilter === platform
                          ? 'bg-[#4A8AE9] text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <i className={`${
                        platform === '小红书' ? 'ri-book-mark-line' :
                        platform === '微博' ? 'ri-weibo-line' :
                        platform === '抖音' ? 'ri-music-line' :
                        'ri-video-line'
                      } text-base`}></i>
                      <span>{platform}</span>
                    </button>
                  ))}
                </div>

                <div className="ml-auto text-sm text-gray-600">
                  共 {overviewStats.totalVolume} 条内容
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">总声量</p>
                      <p className="text-3xl font-bold text-gray-900">{overviewStats.totalVolume.toLocaleString()}</p>
                      <p className="text-xs text-green-600 mt-2 flex items-center">
                        <i className="ri-arrow-up-line mr-1"></i>
                        较上周 +12.5%
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-[#4A8AE9]/10 rounded-lg flex items-center justify-center">
                      <i className="ri-volume-up-line text-[#4A8AE9] text-2xl"></i>
                    </div>
                  </div>
                </Card>
                
                <Card>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">总互动量</p>
                      <p className="text-3xl font-bold text-gray-900">{overviewStats.totalInteraction.toLocaleString()}</p>
                      <p className="text-xs text-green-600 mt-2 flex items-center">
                        <i className="ri-arrow-up-line mr-1"></i>
                        较上周 +18.3%
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <i className="ri-heart-line text-blue-600 text-2xl"></i>
                    </div>
                  </div>
                </Card>
                
                <Card>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">综合好评率</p>
                      <p className="text-3xl font-bold text-gray-900">{overviewStats.positiveRate}%</p>
                      <p className="text-xs text-green-600 mt-2 flex items-center">
                        <i className="ri-arrow-up-line mr-1"></i>
                        较上周 +2.1%
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <i className="ri-thumb-up-line text-green-600 text-2xl"></i>
                    </div>
                  </div>
                </Card>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <h3 className="text-lg font-semibold text-gray-900 mb-6">声量趋势</h3>
                  <div className="h-64 relative">
                    <svg className="w-full h-full" viewBox="0 0 600 250" preserveAspectRatio="none">
                      {/* 网格线 */}
                      <line x1="40" y1="20" x2="40" y2="200" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="40" y1="200" x2="580" y2="200" stroke="#E5E7EB" strokeWidth="2" />
                      
                      {/* 水平参考线 */}
                      {[0, 1, 2, 3, 4].map((i) => (
                        <g key={i}>
                          <line 
                            x1="40" 
                            y1={200 - i * 45} 
                            x2="580" 
                            y2={200 - i * 45} 
                            stroke="#F3F4F6" 
                            strokeWidth="1" 
                            strokeDasharray="4,4"
                          />
                        </g>
                      ))}
                      
                      {/* 折线路径 */}
                      <polyline
                        points={stats.volumeTrend.map((item, idx) => {
                          const maxValue = Math.max(...stats.volumeTrend.map(v => v.value));
                          const x = 40 + (idx * (540 / (stats.volumeTrend.length - 1)));
                          const y = 200 - ((item.value / maxValue) * 180);
                          return `${x},${y}`;
                        }).join(' ')}
                        fill="none"
                        stroke="#4A8AE9"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      
                      {/* 渐变填充区域 */}
                      <defs>
                        <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#4A8AE9" stopOpacity="0.3" />
                          <stop offset="100%" stopColor="#4A8AE9" stopOpacity="0.05" />
                        </linearGradient>
                      </defs>
                      <polygon
                        points={`40,200 ${stats.volumeTrend.map((item, idx) => {
                          const maxValue = Math.max(...stats.volumeTrend.map(v => v.value));
                          const x = 40 + (idx * (540 / (stats.volumeTrend.length - 1)));
                          const y = 200 - ((item.value / maxValue) * 180);
                          return `${x},${y}`;
                        }).join(' ')} 580,200`}
                        fill="url(#areaGradient)"
                      />
                      
                      {/* 数据点 */}
                      {stats.volumeTrend.map((item, idx) => {
                        const maxValue = Math.max(...stats.volumeTrend.map(v => v.value));
                        const x = 40 + (idx * (540 / (stats.volumeTrend.length - 1)));
                        const y = 200 - ((item.value / maxValue) * 180);
                        return (
                          <g key={idx}>
                            <circle
                              cx={x}
                              cy={y}
                              r="5"
                              fill="white"
                              stroke="#4A8AE9"
                              strokeWidth="3"
                              className="cursor-pointer hover:r-7 transition-all"
                            />
                            <circle
                              cx={x}
                              cy={y}
                              r="12"
                              fill="transparent"
                              className="cursor-pointer"
                            >
                              <title>{item.value.toLocaleString()}</title>
                            </circle>
                          </g>
                        );
                      })}
                    </svg>
                    
                    {/* X轴标签 */}
                    <div className="flex justify-between px-10 mt-2">
                      {stats.volumeTrend.map((item, idx) => (
                        <span key={idx} className="text-xs text-gray-600">{item.date}</span>
                      ))}
                    </div>
                    
                    {/* 悬浮提示 */}
                    <div className="absolute top-0 left-0 pointer-events-none">
                      {stats.volumeTrend.map((item, idx) => {
                        const maxValue = Math.max(...stats.volumeTrend.map(v => v.value));
                        const x = 40 + (idx * (540 / (stats.volumeTrend.length - 1)));
                        const y = 200 - ((item.value / maxValue) * 180);
                        return (
                          <div
                            key={idx}
                            className="absolute opacity-0 hover:opacity-100 transition-opacity bg-gray-900 text-white text-xs px-3 py-1.5 rounded shadow-lg whitespace-nowrap"
                            style={{
                              left: `${(x / 600) * 100}%`,
                              top: `${(y / 250) * 100}%`,
                              transform: 'translate(-50%, -120%)'
                            }}
                          >
                            {item.date}: {item.value.toLocaleString()}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </Card>
                
                <Card>
                  <h3 className="text-lg font-semibold text-gray-900 mb-6">平台分布</h3>
                  <div className="space-y-4">
                    {stats.platformDistribution.map((item, idx) => (
                      <div key={idx}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">{item.platform}</span>
                          <span className="text-sm text-gray-600">{item.count.toLocaleString()} ({item.percentage}%)</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div 
                            className="bg-[#4A8AE9] h-2 rounded-full transition-all duration-500"
                            style={{ width: `${item.percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>

              <Card>
                <h3 className="text-lg font-semibold text-gray-900 mb-6">素材形态分布</h3>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                  {stats.contentTypeDistribution.map((item, idx) => (
                    <div key={idx} className="text-center">
                      <div 
                        className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 transition-transform hover:scale-110"
                        style={{ backgroundColor: `${item.color}15` }}
                      >
                        <i className={`${item.icon} text-3xl`} style={{ color: item.color }}></i>
                      </div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-1">{item.type}</h4>
                      <p className="text-2xl font-bold mb-1" style={{ color: item.color }}>
                        {item.count.toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500">{item.percentage}%</p>
                    </div>
                  ))}
                </div>
                
                <div className="mt-8 pt-6">
                  <div className="flex items-center justify-center h-4 rounded-full overflow-hidden">
                    {stats.contentTypeDistribution.map((item, idx) => (
                      <div
                        key={idx}
                        className="h-full transition-all hover:opacity-80 cursor-pointer relative group"
                        style={{ 
                          width: `${item.percentage}%`,
                          backgroundColor: item.color,
                        }}
                      >
                        <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-3 py-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                          {item.type}: {item.count.toLocaleString()} ({item.percentage}%)
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-center gap-6 mt-6">
                    {stats.contentTypeDistribution.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: item.color }}
                        ></div>
                        <span className="text-xs text-gray-600">{item.type}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
              
              <Card>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">实时动态</h3>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('content')}>
                    查看全部
                    <i className="ri-arrow-right-line ml-1"></i>
                  </Button>
                </div>
                <div className="space-y-4">
                  {[
                    { platform: '小红书', user: '艺术爱好者小王', content: '敦煌艺术特展真的太震撼了！灯光设计特别用心...', time: '2分钟前', sentiment: 'positive' },
                    { platform: '微博', user: '博物馆观察员', content: '从专业角度来看,这次敦煌艺术特展的策展水平相当高...', time: '15分钟前', sentiment: 'positive' },
                    { platform: '抖音', user: '文化探索者', content: '周末打卡敦煌艺术特展！氛围感拉满...', time: '32分钟前', sentiment: 'positive' },
                    { platform: '视频号', user: '艺术史研究者', content: '敦煌艺术特展的学术价值很高,展品选择精良...', time: '1小时前', sentiment: 'positive' },
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="w-10 h-10 bg-[#4A8AE9]/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-user-line text-[#4A8AE9]"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-gray-900">{item.user}</span>
                          <span className={`px-2 py-0.5 text-xs rounded whitespace-nowrap ${getPlatformBadgeStyle(item.platform)}`}>
                            {item.platform}
                          </span>
                          <span className="text-xs text-gray-500">·</span>
                          <span className="text-xs text-gray-500">{item.time}</span>
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2">{item.content}</p>
                      </div>
                      <div className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${
                        item.sentiment === 'positive' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {item.sentiment === 'positive' ? '正面' : '中性'}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}
          
          {activeTab === 'dimension' && (
            <div className="space-y-6">
              {/* 维度 Tab 切换 */}
              <div className="bg-white rounded-lg border border-gray-200 p-2">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setDimensionTab('overview')}
                    className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 ${
                      dimensionTab === 'overview'
                        ? 'bg-[#4A8AE9] text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <i className="ri-dashboard-line"></i>
                    <span>总览</span>
                  </button>
                  {['content', 'design', 'interaction', 'activities', 'service'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setDimensionTab(tab as any)}
                      className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 ${
                        dimensionTab === tab
                          ? 'bg-[#4A8AE9] text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <i className={getDimensionIcon(tab)}></i>
                      <span>{getDimensionName(tab)}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* 总览 Tab */}
              {dimensionTab === 'overview' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* 雷达图 */}
                  <Card>
                    <h3 className="text-lg font-semibold text-gray-900 mb-6">五维评价雷达图</h3>
                    <div className="h-96 flex items-center justify-center">
                      <div className="relative w-80 h-80">
                        <svg viewBox="0 0 200 200" className="w-full h-full">
                          {/* 背景网格 */}
                          <polygon
                            points="100,20 173,65 155,150 45,150 27,65"
                            fill="none"
                            stroke="#e5e7eb"
                            strokeWidth="1"
                          />
                          <polygon
                            points="100,50 146,82.5 127.5,125 72.5,125 54,82.5"
                            fill="none"
                            stroke="#e5e7eb"
                            strokeWidth="1"
                          />
                          
                          {/* 连接线 */}
                          <line x1="100" y1="100" x2="100" y2="20" stroke="#e5e7eb" strokeWidth="1" />
                          <line x1="100" y1="100" x2="173" y2="65" stroke="#e5e7eb" strokeWidth="1" />
                          <line x1="100" y1="100" x2="155" y2="150" stroke="#e5e7eb" strokeWidth="1" />
                          <line x1="100" y1="100" x2="45" y2="150" stroke="#e5e7eb" strokeWidth="1" />
                          <line x1="100" y1="100" x2="27" y2="65" stroke="#e5e7eb" strokeWidth="1" />
                          
                          {/* 数据区域 */}
                          <polygon
                            points="100,28 165,68 148,142 52,142 35,68"
                            fill="rgba(74, 138, 233, 0.2)"
                            stroke="#4A8AE9"
                            strokeWidth="2"
                          />
                          
                          {/* 数据点 */}
                          <circle cx="100" cy="28" r="4" fill="#4A8AE9" />
                          <circle cx="165" cy="68" r="4" fill="#4A8AE9" />
                          <circle cx="148" cy="142" r="4" fill="#4A8AE9" />
                          <circle cx="52" cy="142" r="4" fill="#4A8AE9" />
                          <circle cx="35" cy="68" r="4" fill="#4A8AE9" />
                        </svg>
                        
                        {/* 标签 */}
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-8 text-center">
                          <div className="text-xs font-medium text-gray-700">内容</div>
                          <div className="text-lg font-bold text-[#4A8AE9]">{mockDimensionAnalysisV3.content.score}</div>
                        </div>
                        <div className="absolute top-16 right-0 translate-x-16 text-center">
                          <div className="text-xs font-medium text-gray-700">设计</div>
                          <div className="text-lg font-bold text-[#4A8AE9]">{mockDimensionAnalysisV3.design.score}</div>
                        </div>
                        <div className="absolute bottom-8 right-4 translate-x-16 text-center">
                          <div className="text-xs font-medium text-gray-700">互动</div>
                          <div className="text-lg font-bold text-[#4A8AE9]">{mockDimensionAnalysisV3.interaction.score}</div>
                        </div>
                        <div className="absolute bottom-8 left-4 -translate-x-16 text-center">
                          <div className="text-xs font-medium text-gray-700">活动</div>
                          <div className="text-lg font-bold text-[#4A8AE9]">{mockDimensionAnalysisV3.activities.score}</div>
                        </div>
                        <div className="absolute top-16 left-0 -translate-x-16 text-center">
                          <div className="text-xs font-medium text-gray-700">服务</div>
                          <div className="text-lg font-bold text-[#4A8AE9]">{mockDimensionAnalysisV3.service.score}</div>
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* 各平台维度声量分布 */}
                  <Card>
                    <h3 className="text-lg font-semibold text-gray-900 mb-6">各平台维度声量分布</h3>
                    <div className="space-y-6">
                      {selectedProject.platforms.map((platform) => {
                        const platformData = mockDimensionAnalysisV3.overview.platformDimensionTrend[platform as keyof typeof mockDimensionAnalysisV3.overview.platformDimensionTrend];
                        const contentTotal = platformData.content.reduce((a, b) => a + b, 0);
                        const designTotal = platformData.design.reduce((a, b) => a + b, 0);
                        const interactionTotal = platformData.interaction.reduce((a, b) => a + b, 0);
                        const activitiesTotal = platformData.activities.reduce((a, b) => a + b, 0);
                        const serviceTotal = platformData.service.reduce((a, b) => a + b, 0);
                        const total = contentTotal + designTotal + interactionTotal + activitiesTotal + serviceTotal;
                        
                        const contentPercent = (contentTotal / total) * 100;
                        const designPercent = (designTotal / total) * 100;
                        const interactionPercent = (interactionTotal / total) * 100;
                        const activitiesPercent = (activitiesTotal / total) * 100;
                        const servicePercent = (serviceTotal / total) * 100;
                        
                        return (
                          <div key={platform}>
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <i className={`${
                                  platform === '小红书' ? 'ri-book-mark-line' :
                                  platform === '微博' ? 'ri-weibo-line' :
                                  platform === '抖音' ? 'ri-music-line' :
                                  'ri-video-line'
                                } text-gray-700`}></i>
                                <span className="text-sm font-medium text-gray-900">{platform}</span>
                              </div>
                              <span className="text-sm text-gray-600">{total.toLocaleString()}</span>
                            </div>
                            <div className="flex h-5 rounded-lg overflow-hidden">
                              <div 
                                className="bg-[#4A8AE9] hover:opacity-80 transition-opacity cursor-pointer relative group"
                                style={{ width: `${contentPercent}%` }}
                              >
                                <div className="absolute -top-16 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-3 py-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                  内容: {contentTotal.toLocaleString()} ({contentPercent.toFixed(1)}%)
                                </div>
                              </div>
                              <div 
                                className="bg-[#10B981] hover:opacity-80 transition-opacity cursor-pointer relative group"
                                style={{ width: `${designPercent}%` }}
                              >
                                <div className="absolute -top-16 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-3 py-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                  设计: {designTotal.toLocaleString()} ({designPercent.toFixed(1)}%)
                                </div>
                              </div>
                              <div 
                                className="bg-[#F59E0B] hover:opacity-80 transition-opacity cursor-pointer relative group"
                                style={{ width: `${interactionPercent}%` }}
                              >
                                <div className="absolute -top-16 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-3 py-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                  互动: {interactionTotal.toLocaleString()} ({interactionPercent.toFixed(1)}%)
                                </div>
                              </div>
                              <div 
                                className="bg-[#8B5CF6] hover:opacity-80 transition-opacity cursor-pointer relative group"
                                style={{ width: `${activitiesPercent}%` }}
                              >
                                <div className="absolute -top-16 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-3 py-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                  活动: {activitiesTotal.toLocaleString()} ({activitiesPercent.toFixed(1)}%)
                                </div>
                              </div>
                              <div 
                                className="bg-[#EC4899] hover:opacity-80 transition-opacity cursor-pointer relative group"
                                style={{ width: `${servicePercent}%` }}
                              >
                                <div className="absolute -top-16 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-3 py-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                  服务: {serviceTotal.toLocaleString()} ({servicePercent.toFixed(1)}%)
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* 图例 */}
                    <div className="flex items-center justify-center gap-6 mt-8">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-[#4A8AE9]"></div>
                        <span className="text-xs text-gray-600">内容</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-[#10B981]"></div>
                        <span className="text-xs text-gray-600">设计</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-[#F59E0B]"></div>
                        <span className="text-xs text-gray-600">互动</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-[#8B5CF6]"></div>
                        <span className="text-xs text-gray-600">活动</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-[#EC4899]"></div>
                        <span className="text-xs text-gray-600">服务</span>
                      </div>
                    </div>
                  </Card>

                  {/* AI 智能观点提取 - 左右布局 */}
                  <Card className="lg:col-span-2">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                      <i className="ri-lightbulb-flash-line text-[#4A8AE9]"></i>
                      AI 智能观点提取
                    </h3>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* 左侧：正面评价 TOP3 */}
                      <div className="bg-green-50/50 rounded-xl p-5 border border-green-100">
                        <h4 className="text-sm font-semibold text-green-700 mb-4 flex items-center gap-2">
                          <i className="ri-thumb-up-fill"></i>
                          正面评价 TOP3
                        </h4>
                        <div className="space-y-3">
                          {mockDimensionAnalysisV3.overview.highlights.slice(0, 3).map((item, idx) => (
                            <div key={idx} className="rounded-lg bg-white border border-green-100 overflow-hidden">
                              <div className="flex items-start gap-3 p-3">
                                <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 rounded-full bg-green-100 text-green-700 text-sm font-bold">
                                  {idx + 1}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-green-900 leading-relaxed mb-2">{item.text}</p>
                                  <div className="flex items-center gap-3 text-xs">
                                    <span className="px-2.5 py-1 rounded-full bg-green-100 text-green-700 font-medium">
                                      {item.count.toLocaleString()} 次提及
                                    </span>
                                    <span className="text-gray-500">
                                      来自 {Math.round((item.count / mockDimensionAnalysisV3.overview.total) * 100)}% 的内容
                                    </span>
                                  </div>
                                </div>
                              </div>
                              {/* 摘要说明 */}
                              <div className="px-3 pb-3 pt-0">
                                <div className="bg-green-50 rounded-lg p-2.5 border border-green-100">
                                  <div className="flex items-start gap-2">
                                    <i className="ri-chat-quote-line text-green-600 text-xs mt-0.5 flex-shrink-0"></i>
                                    <p className="text-xs text-green-800 leading-relaxed">
                                      {idx === 0 && '观众普遍认为展览通过精心的灯光、布景和多媒体设计，成功营造出沉浸式的观展体验，让人仿佛置身于千年前的敦煌洞窟之中。'}
                                      {idx === 1 && '展品选择精良，涵盖了敦煌艺术的各个时期和类型，具有很高的历史价值和艺术价值，让观众能够全面了解敦煌文化的精髓。'}
                                      {idx === 2 && '灯光设计获得专业人士和普通观众的一致好评，既保护了展品，又通过光影效果突出了壁画的细节和色彩，增强了视觉冲击力。'}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* 右侧：负面评价 TOP3 */}
                      <div className="bg-red-50/50 rounded-xl p-5 border border-red-100">
                        <h4 className="text-sm font-semibold text-red-700 mb-4 flex items-center gap-2">
                          <i className="ri-thumb-down-fill"></i>
                          负面评价 TOP3
                        </h4>
                        <div className="space-y-3">
                          {mockDimensionAnalysisV3.overview.weaknesses.slice(0, 3).map((item, idx) => (
                            <div key={idx} className="rounded-lg bg-white border border-red-100 overflow-hidden">
                              <div className="flex items-start gap-3 p-3">
                                <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 rounded-full bg-red-100 text-red-700 text-sm font-bold">
                                  {idx + 1}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-red-900 leading-relaxed mb-2">{item.text}</p>
                                  <div className="flex items-center gap-3 text-xs">
                                    <span className="px-2.5 py-1 rounded-full bg-red-100 text-red-700 font-medium">
                                      {item.count.toLocaleString()} 次提及
                                    </span>
                                    <span className="text-gray-500">
                                      来自 {Math.round((item.count / mockDimensionAnalysisV3.overview.total) * 100)}% 的内容
                                    </span>
                                  </div>
                                </div>
                              </div>
                              {/* 摘要说明 */}
                              <div className="px-3 pb-3 pt-0">
                                <div className="bg-red-50 rounded-lg p-2.5 border border-red-100">
                                  <div className="flex items-start gap-2">
                                    <i className="ri-chat-quote-line text-red-600 text-xs mt-0.5 flex-shrink-0"></i>
                                    <p className="text-xs text-red-800 leading-relaxed">
                                      {idx === 0 && '周末和节假日期间观众数量激增，导致观展体验下降。建议增加限流措施或延长开放时间，引导观众选择工作日参观。'}
                                      {idx === 1 && '部分观众反映展厅内的指引标识不够清晰，容易迷路或走回头路。建议优化动线设计，增加更多明显的指示牌和地面标识。'}
                                      {idx === 2 && '相比展览的规模和内容，互动体验区域显得较少，部分观众希望能有更多参与式的体验项目，增强互动性和趣味性。'}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* 专项语义词云 - 左右布局气泡图 */}
                  <Card className="lg:col-span-2">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6">专项语义词云</h3>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* 左侧：正面词云气泡图 */}
                      <div className="bg-green-50/30 rounded-xl p-5 border border-green-100">
                        <h4 className="text-sm font-semibold text-green-700 mb-4 flex items-center gap-2">
                          <i className="ri-emotion-happy-line"></i>
                          正面关键词
                        </h4>
                        <div className="relative h-72 bg-white rounded-lg flex items-center justify-center overflow-hidden border border-green-100">
                          <div className="relative w-full h-full p-4">
                            {/* 气泡图布局 */}
                            {[
                              { word: '震撼', size: 56, x: 50, y: 35, color: '#10B981' },
                              { word: '精美', size: 48, x: 25, y: 55, color: '#059669' },
                              { word: '值得', size: 44, x: 75, y: 60, color: '#34D399' },
                              { word: '推荐', size: 40, x: 35, y: 75, color: '#10B981' },
                              { word: '氛围', size: 38, x: 65, y: 25, color: '#059669' },
                              { word: '灯光', size: 36, x: 20, y: 30, color: '#34D399' },
                              { word: '布景', size: 34, x: 80, y: 45, color: '#10B981' },
                              { word: '壮观', size: 32, x: 55, y: 80, color: '#059669' },
                              { word: '感动', size: 30, x: 15, y: 70, color: '#34D399' },
                              { word: '美丽', size: 28, x: 85, y: 75, color: '#10B981' },
                              { word: '精致', size: 26, x: 40, y: 20, color: '#059669' },
                              { word: '喜欢', size: 24, x: 70, y: 85, color: '#34D399' },
                            ].map((bubble, idx) => (
                              <div
                                key={idx}
                                className="absolute transform -translate-x-1/2 -translate-y-1/2 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer shadow-sm"
                                style={{
                                  left: `${bubble.x}%`,
                                  top: `${bubble.y}%`,
                                  width: `${bubble.size}px`,
                                  height: `${bubble.size}px`,
                                  backgroundColor: `${bubble.color}20`,
                                  border: `2px solid ${bubble.color}40`,
                                }}
                              >
                                <span
                                  className="font-medium text-center leading-tight"
                                  style={{
                                    fontSize: `${Math.max(10, bubble.size / 4.5)}px`,
                                    color: bubble.color,
                                  }}
                                >
                                  {bubble.word}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* 右侧：负面词云气泡图 */}
                      <div className="bg-red-50/30 rounded-xl p-5 border border-red-100">
                        <h4 className="text-sm font-semibold text-red-700 mb-4 flex items-center gap-2">
                          <i className="ri-emotion-unhappy-line"></i>
                          负面关键词
                        </h4>
                        <div className="relative h-72 bg-white rounded-lg flex items-center justify-center overflow-hidden border border-red-100">
                          <div className="relative w-full h-full p-4">
                            {/* 气泡图布局 */}
                            {[
                              { word: '人多', size: 52, x: 45, y: 40, color: '#EF4444' },
                              { word: '排队', size: 46, x: 70, y: 55, color: '#DC2626' },
                              { word: '拥挤', size: 42, x: 25, y: 60, color: '#F87171' },
                              { word: '太暗', size: 38, x: 55, y: 75, color: '#EF4444' },
                              { word: '指引', size: 36, x: 30, y: 30, color: '#DC2626' },
                              { word: '不清', size: 34, x: 75, y: 25, color: '#F87171' },
                              { word: '互动少', size: 32, x: 20, y: 80, color: '#EF4444' },
                              { word: '票价', size: 30, x: 80, y: 70, color: '#DC2626' },
                              { word: '等待', size: 28, x: 50, y: 20, color: '#F87171' },
                              { word: '嘈杂', size: 26, x: 85, y: 45, color: '#EF4444' },
                              { word: '座位', size: 24, x: 15, y: 45, color: '#DC2626' },
                            ].map((bubble, idx) => (
                              <div
                                key={idx}
                                className="absolute transform -translate-x-1/2 -translate-y-1/2 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer shadow-sm"
                                style={{
                                  left: `${bubble.x}%`,
                                  top: `${bubble.y}%`,
                                  width: `${bubble.size}px`,
                                  height: `${bubble.size}px`,
                                  backgroundColor: `${bubble.color}20`,
                                  border: `2px solid ${bubble.color}40`,
                                }}
                              >
                                <span
                                  className="font-medium text-center leading-tight"
                                  style={{
                                    fontSize: `${Math.max(10, bubble.size / 4.5)}px`,
                                    color: bubble.color,
                                  }}
                                >
                                  {bubble.word}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
              )}

              {/* 其他维度 Tab */}
              {dimensionTab !== 'overview' && (
                <div className="space-y-6">
                  {/* 各平台/各角色情感分布图 - 所有维度都显示 */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* 各平台情感分布 */}
                    <Card>
                      <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                        <i className="ri-apps-line text-[#4A8AE9]"></i>
                        各平台观点分布
                        <span className="text-xs font-normal text-gray-400 ml-auto">
                          共 {(mockDimensionAnalysisV3[dimensionTab as keyof typeof mockDimensionAnalysisV3] as any)?.total?.toLocaleString() || '0'} 条
                        </span>
                      </h4>
                      <div className="space-y-4">
                        {(() => {
                          const platformSentiments = selectedProject.platforms.map(platform => {
                            const platformContents = mockContents.filter(c => c.platform === platform);
                            const total = platformContents.length;
                            const positive = platformContents.filter(c => c.sentiment === 'positive').length;
                            const neutral = platformContents.filter(c => c.sentiment === 'neutral').length;
                            const negative = platformContents.filter(c => c.sentiment === 'negative').length;
                            return {
                              platform,
                              total,
                              positive,
                              neutral,
                              negative,
                              positivePercent: total > 0 ? (positive / total) * 100 : 0,
                              neutralPercent: total > 0 ? (neutral / total) * 100 : 0,
                              negativePercent: total > 0 ? (negative / total) * 100 : 0,
                            };
                          });
                          
                          return platformSentiments.map((item, idx) => (
                            <div key={idx}>
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <i className={`${
                                    item.platform === '小红书' ? 'ri-book-mark-line' :
                                    item.platform === '微博' ? 'ri-weibo-line' :
                                    item.platform === '抖音' ? 'ri-music-line' :
                                    'ri-video-line'
                                  } text-gray-700 text-sm`}></i>
                                  <span className="text-sm font-medium text-gray-900">{item.platform}</span>
                                </div>
                                <div className="flex items-center gap-3 text-xs">
                                  <span className="text-green-600">{item.positive} 正面</span>
                                  <span className="text-gray-500">{item.neutral} 中性</span>
                                  <span className="text-red-500">{item.negative} 负面</span>
                                </div>
                              </div>
                              <div className="flex h-6 rounded-lg overflow-hidden bg-gray-100">
                                <div 
                                  className="bg-green-500 hover:bg-green-600 transition-colors cursor-pointer relative group flex items-center justify-center"
                                  style={{ width: `${item.positivePercent}%` }}
                                >
                                  {item.positivePercent > 15 && (
                                    <span className="text-xs text-white font-medium">{item.positivePercent.toFixed(0)}%</span>
                                  )}
                                  <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                    正面: {item.positive} ({item.positivePercent.toFixed(1)}%)
                                  </div>
                                </div>
                                <div 
                                  className="bg-gray-400 hover:bg-gray-500 transition-colors cursor-pointer relative group flex items-center justify-center"
                                  style={{ width: `${item.neutralPercent}%` }}
                                >
                                  {item.neutralPercent > 15 && (
                                    <span className="text-xs text-white font-medium">{item.neutralPercent.toFixed(0)}%</span>
                                  )}
                                  <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                    中性: {item.neutral} ({item.neutralPercent.toFixed(1)}%)
                                  </div>
                                </div>
                                <div 
                                  className="bg-red-500 hover:bg-red-600 transition-colors cursor-pointer relative group flex items-center justify-center"
                                  style={{ width: `${item.negativePercent}%` }}
                                >
                                  {item.negativePercent > 15 && (
                                    <span className="text-xs text-white font-medium">{item.negativePercent.toFixed(0)}%</span>
                                  )}
                                  <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                    负面: {item.negative} ({item.negativePercent.toFixed(1)}%)
                                  </div>
                                </div>
                              </div>
                            </div>
                          ));
                        })()}
                      </div>
                    </Card>

                    {/* 各角色情感分布 */}
                    <Card>
                      <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                        <i className="ri-user-line text-[#4A8AE9]"></i>
                        各角色观点分布
                        <span className="text-xs font-normal text-gray-400 ml-auto">
                          共 {(mockDimensionAnalysisV3[dimensionTab as keyof typeof mockDimensionAnalysisV3] as any)?.total?.toLocaleString() || '0'} 条
                        </span>
                      </h4>
                      <div className="space-y-4">
                        {(() => {
                          const roles = ['观众', 'KOL', '官方', '专家'];
                          const roleSentiments = roles.map(role => {
                            const roleContents = mockContents.filter(c => c.authorType === role);
                            const total = roleContents.length;
                            const positive = roleContents.filter(c => c.sentiment === 'positive').length;
                            const neutral = roleContents.filter(c => c.sentiment === 'neutral').length;
                            const negative = roleContents.filter(c => c.sentiment === 'negative').length;
                            return {
                              role,
                              total,
                              positive,
                              neutral,
                              negative,
                              positivePercent: total > 0 ? (positive / total) * 100 : 0,
                              neutralPercent: total > 0 ? (neutral / total) * 100 : 0,
                              negativePercent: total > 0 ? (negative / total) * 100 : 0,
                            };
                          });
                          
                          return roleSentiments.map((item, idx) => (
                            <div key={idx}>
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <i className={`${
                                    item.role === '观众' ? 'ri-user-line' :
                                    item.role === 'KOL' ? 'ri-star-line' :
                                    item.role === '官方' ? 'ri-shield-check-line' :
                                    'ri-graduation-cap-line'
                                  } text-gray-700 text-sm`}></i>
                                  <span className="text-sm font-medium text-gray-900">{item.role}</span>
                                </div>
                                <div className="flex items-center gap-3 text-xs">
                                  <span className="text-green-600">{item.positive} 正面</span>
                                  <span className="text-gray-500">{item.neutral} 中性</span>
                                  <span className="text-red-500">{item.negative} 负面</span>
                                </div>
                              </div>
                              <div className="flex h-6 rounded-lg overflow-hidden bg-gray-100">
                                <div 
                                  className="bg-green-500 hover:bg-green-600 transition-colors cursor-pointer relative group flex items-center justify-center"
                                  style={{ width: `${item.positivePercent}%` }}
                                >
                                  {item.positivePercent > 15 && (
                                    <span className="text-xs text-white font-medium">{item.positivePercent.toFixed(0)}%</span>
                                  )}
                                  <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                    正面: {item.positive} ({item.positivePercent.toFixed(1)}%)
                                  </div>
                                </div>
                                <div 
                                  className="bg-gray-400 hover:bg-gray-500 transition-colors cursor-pointer relative group flex items-center justify-center"
                                  style={{ width: `${item.neutralPercent}%` }}
                                >
                                  {item.neutralPercent > 15 && (
                                    <span className="text-xs text-white font-medium">{item.neutralPercent.toFixed(0)}%</span>
                                  )}
                                  <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                    中性: {item.neutral} ({item.neutralPercent.toFixed(1)}%)
                                  </div>
                                </div>
                                <div 
                                  className="bg-red-500 hover:bg-red-600 transition-colors cursor-pointer relative group flex items-center justify-center"
                                  style={{ width: `${item.negativePercent}%` }}
                                >
                                  {item.negativePercent > 15 && (
                                    <span className="text-xs text-white font-medium">{item.negativePercent.toFixed(0)}%</span>
                                  )}
                                  <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                    负面: {item.negative} ({item.negativePercent.toFixed(1)}%)
                                  </div>
                                </div>
                              </div>
                            </div>
                          ));
                        })()}
                      </div>
                    </Card>
                  </div>

                  {/* 智能观点提取 */}
                  <Card>
                    <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                      <i className="ri-lightbulb-flash-line text-[#4A8AE9]"></i>
                      智能观点提取
                      <span className="text-xs font-normal text-gray-400 ml-2">
                        基于 {(mockDimensionAnalysisV3[dimensionTab as keyof typeof mockDimensionAnalysisV3] as any)?.total?.toLocaleString() || '0'} 条观点内容分析
                      </span>
                    </h3>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* 左侧：正面观点 */}
                      <div className="bg-green-50/50 rounded-xl p-5 border border-green-100">
                        <h5 className="text-sm font-semibold text-green-700 mb-4 flex items-center gap-2">
                          <i className="ri-thumb-up-fill"></i>
                          正面观点
                        </h5>
                        
                        {/* 正面词云 */}
                        <div className="relative h-48 bg-white rounded-lg mb-4 flex items-center justify-center overflow-hidden border border-green-100">
                          <div className="relative w-full h-full flex flex-wrap items-center justify-center gap-2 p-4">
                            {(mockDimensionAnalysisV3[dimensionTab] as any).positiveWordCloud?.map((word: { word: string; size: number; color: string }, idx: number) => (
                              <div
                                key={idx}
                                className="transition-all duration-300 hover:scale-110 cursor-pointer"
                                style={{
                                  fontSize: `${word.size * 0.6}px`,
                                  color: word.color,
                                  fontWeight: word.size > 35 ? 'bold' : word.size > 25 ? '600' : 'normal',
                                  opacity: 0.8 + (word.size / 100),
                                }}
                              >
                                {word.word}
                              </div>
                            )) || (
                              <span className="text-gray-400 text-sm">暂无词云数据</span>
                            )}
                          </div>
                        </div>
                        
                        {/* 正面AI总结 */}
                        <div className="bg-white rounded-lg p-4 border border-green-100">
                          <div className="flex items-center gap-2 mb-2">
                            <i className="ri-robot-line text-green-600"></i>
                            <span className="text-xs font-medium text-green-700">AI 总结摘要</span>
                          </div>
                          <p className="text-sm text-gray-700 leading-relaxed">
                            {(mockDimensionAnalysisV3[dimensionTab] as any).positiveSummary || '观众对该维度整体评价积极，认为展览在此方面表现出色，值得推荐。'}
                          </p>
                        </div>
                      </div>

                      {/* 右侧：负面观点 */}
                      <div className="bg-red-50/50 rounded-xl p-5 border border-red-100">
                        <h5 className="text-sm font-semibold text-red-700 mb-4 flex items-center gap-2">
                          <i className="ri-thumb-down-fill"></i>
                          负面观点
                        </h5>
                        
                        {/* 负面词云 */}
                        <div className="relative h-48 bg-white rounded-lg mb-4 flex items-center justify-center overflow-hidden border border-red-100">
                          <div className="relative w-full h-full flex flex-wrap items-center justify-center gap-2 p-4">
                            {(mockDimensionAnalysisV3[dimensionTab] as any).negativeWordCloud?.map((word: { word: string; size: number; color: string }, idx: number) => (
                              <div
                                key={idx}
                                className="transition-all duration-300 hover:scale-110 cursor-pointer"
                                style={{
                                  fontSize: `${word.size * 0.6}px`,
                                  color: word.color,
                                  fontWeight: word.size > 35 ? 'bold' : word.size > 25 ? '600' : 'normal',
                                  opacity: 0.8 + (word.size / 100),
                                }}
                              >
                                {word.word}
                              </div>
                            )) || (
                              <span className="text-gray-400 text-sm">暂无词云数据</span>
                            )}
                          </div>
                        </div>
                        
                        {/* 负面AI总结 */}
                        <div className="bg-white rounded-lg p-4 border border-red-100">
                          <div className="flex items-center gap-2 mb-2">
                            <i className="ri-robot-line text-red-600"></i>
                            <span className="text-xs font-medium text-red-700">AI 总结摘要</span>
                          </div>
                          <p className="text-sm text-gray-700 leading-relaxed">
                            {(mockDimensionAnalysisV3[dimensionTab] as any).negativeSummary || '部分观众对该维度提出了改进建议，主要集中在体验优化和服务提升方面。'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* 原声内容明细 */}
                  <Card>
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <i className="ri-chat-voice-line text-[#4A8AE9]"></i>
                        原声内容明细
                        <span className="text-xs font-normal text-gray-500 ml-2">
                          （已智能提取与「{getDimensionName(dimensionTab)}」相关的观点）
                        </span>
                      </h3>
                    </div>
                    
                    {/* 筛选栏 */}
                    <div className="flex items-center gap-3 mb-4 flex-wrap">
                      {/* 情感筛选 */}
                      <div className="relative">
                        <button
                          onClick={() => {
                            setShowDimensionSentimentDropdown(!showDimensionSentimentDropdown);
                            setShowDimensionPlatformDropdown(false);
                            setShowDimensionRoleDropdown(false);
                            setShowDimensionTypeDropdown(false);
                            setShowDimensionTimeDropdown(false);
                          }}
                          className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className={`${
                            dimensionContentSentiment === 'positive' ? 'ri-thumb-up-line text-green-600' :
                            dimensionContentSentiment === 'neutral' ? 'ri-chat-3-line text-gray-600' :
                            dimensionContentSentiment === 'negative' ? 'ri-thumb-down-line text-red-600' :
                            'ri-emotion-line text-gray-600'
                          } w-4 h-4 flex items-center justify-center`}></i>
                          <span>
                            {dimensionContentSentiment === 'all' ? '情感' : 
                             dimensionContentSentiment === 'positive' ? '正面' :
                             dimensionContentSentiment === 'neutral' ? '中性' : '负面'}
                          </span>
                          <i className="ri-arrow-down-s-line text-gray-400"></i>
                        </button>
                        
                        {showDimensionSentimentDropdown && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setShowDimensionSentimentDropdown(false)}></div>
                            <div className="absolute top-full left-0 mt-2 w-40 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-20">
                              {[
                                { value: 'all', label: '全部情感', icon: 'ri-emotion-line', color: '' },
                                { value: 'positive', label: '正面', icon: 'ri-thumb-up-line', color: 'text-green-600' },
                                { value: 'neutral', label: '中性', icon: 'ri-chat-3-line', color: 'text-gray-600' },
                                { value: 'negative', label: '负面', icon: 'ri-thumb-down-line', color: 'text-red-600' },
                              ].map((item) => (
                                <button
                                  key={item.value}
                                  onClick={() => {
                                    setDimensionContentSentiment(item.value as any);
                                    setShowDimensionSentimentDropdown(false);
                                  }}
                                  className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-3 ${
                                    dimensionContentSentiment === item.value ? 'text-[#4A8AE9] bg-[#4A8AE9]/5' : 'text-gray-700'
                                  }`}
                                >
                                  <i className={`${item.icon} w-4 h-4 flex items-center justify-center ${item.color}`}></i>
                                  <span>{item.label}</span>
                                  {dimensionContentSentiment === item.value && <i className="ri-check-line ml-auto text-[#4A8AE9]"></i>}
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>

                      {/* 平台筛选 */}
                      <div className="relative">
                        <button
                          onClick={() => {
                            setShowDimensionPlatformDropdown(!showDimensionPlatformDropdown);
                            setShowDimensionSentimentDropdown(false);
                            setShowDimensionRoleDropdown(false);
                            setShowDimensionTypeDropdown(false);
                            setShowDimensionTimeDropdown(false);
                          }}
                          className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-apps-line w-4 h-4 flex items-center justify-center text-gray-600"></i>
                          <span>{dimensionContentPlatform === 'all' ? '平台' : dimensionContentPlatform}</span>
                          <i className="ri-arrow-down-s-line text-gray-400"></i>
                        </button>
                        
                        {showDimensionPlatformDropdown && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setShowDimensionPlatformDropdown(false)}></div>
                            <div className="absolute top-full left-0 mt-2 w-40 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-20">
                              <button
                                onClick={() => {
                                  setDimensionContentPlatform('all');
                                  setShowDimensionPlatformDropdown(false);
                                }}
                                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-3 ${
                                  dimensionContentPlatform === 'all' ? 'text-[#4A8AE9] bg-[#4A8AE9]/5' : 'text-gray-700'
                                }`}
                              >
                                <i className="ri-apps-line w-4 h-4 flex items-center justify-center"></i>
                                <span>全部平台</span>
                                {dimensionContentPlatform === 'all' && <i className="ri-check-line ml-auto text-[#4A8AE9]"></i>}
                              </button>
                              {selectedProject.platforms.map((platform) => (
                                <button
                                  key={platform}
                                  onClick={() => {
                                    setDimensionContentPlatform(platform);
                                    setShowDimensionPlatformDropdown(false);
                                  }}
                                  className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-3 ${
                                    dimensionContentPlatform === platform ? 'text-[#4A8AE9] bg-[#4A8AE9]/5' : 'text-gray-700'
                                  }`}
                                >
                                  <i className={`${
                                    platform === '小红书' ? 'ri-book-mark-line' :
                                    platform === '微博' ? 'ri-weibo-line' :
                                    platform === '抖音' ? 'ri-music-line' :
                                    'ri-video-line'
                                  } w-4 h-4 flex items-center justify-center`}></i>
                                  <span>{platform}</span>
                                  {dimensionContentPlatform === platform && <i className="ri-check-line ml-auto text-[#4A8AE9]"></i>}
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>

                      {/* 角色筛选 */}
                      <div className="relative">
                        <button
                          onClick={() => {
                            setShowDimensionRoleDropdown(!showDimensionRoleDropdown);
                            setShowDimensionSentimentDropdown(false);
                            setShowDimensionPlatformDropdown(false);
                            setShowDimensionTypeDropdown(false);
                            setShowDimensionTimeDropdown(false);
                          }}
                          className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-user-line w-4 h-4 flex items-center justify-center text-gray-600"></i>
                          <span>{dimensionContentRole === 'all' ? '角色' : dimensionContentRole}</span>
                          <i className="ri-arrow-down-s-line text-gray-400"></i>
                        </button>
                        
                        {showDimensionRoleDropdown && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setShowDimensionRoleDropdown(false)}></div>
                            <div className="absolute top-full left-0 mt-2 w-40 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-20">
                              <button
                                onClick={() => {
                                  setDimensionContentRole('all');
                                  setShowDimensionRoleDropdown(false);
                                }}
                                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-3 ${
                                  dimensionContentRole === 'all' ? 'text-[#4A8AE9] bg-[#4A8AE9]/5' : 'text-gray-700'
                                }`}
                              >
                                <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                                <span>全部角色</span>
                                {dimensionContentRole === 'all' && <i className="ri-check-line ml-auto text-[#4A8AE9]"></i>}
                              </button>
                              {['观众', 'KOL', '官方', '专家'].map((role) => (
                                <button
                                  key={role}
                                  onClick={() => {
                                    setDimensionContentRole(role);
                                    setShowDimensionRoleDropdown(false);
                                  }}
                                  className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-3 ${
                                    dimensionContentRole === role ? 'text-[#4A8AE9] bg-[#4A8AE9]/5' : 'text-gray-700'
                                  }`}
                                >
                                  <i className={`${
                                    role === '观众' ? 'ri-user-line' :
                                    role === 'KOL' ? 'ri-star-line' :
                                    role === '官方' ? 'ri-shield-check-line' :
                                    'ri-graduation-cap-line'
                                  } w-4 h-4 flex items-center justify-center`}></i>
                                  <span>{role}</span>
                                  {dimensionContentRole === role && <i className="ri-check-line ml-auto text-[#4A8AE9]"></i>}
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>

                      {/* 帖子/评论筛选 */}
                      <div className="relative">
                        <button
                          onClick={() => {
                            setShowDimensionTypeDropdown(!showDimensionTypeDropdown);
                            setShowDimensionSentimentDropdown(false);
                            setShowDimensionPlatformDropdown(false);
                            setShowDimensionRoleDropdown(false);
                            setShowDimensionTimeDropdown(false);
                          }}
                          className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-file-list-line w-4 h-4 flex items-center justify-center text-gray-600"></i>
                          <span>
                            {dimensionContentType === 'all' ? '帖子/评论' : 
                             dimensionContentType === 'post' ? '帖子' : '评论'}
                          </span>
                          <i className="ri-arrow-down-s-line text-gray-400"></i>
                        </button>
                        
                        {showDimensionTypeDropdown && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setShowDimensionTypeDropdown(false)}></div>
                            <div className="absolute top-full left-0 mt-2 w-40 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-20">
                              {[
                                { value: 'all', label: '全部类型', icon: 'ri-file-list-line' },
                                { value: 'post', label: '帖子', icon: 'ri-article-line' },
                                { value: 'comment', label: '评论', icon: 'ri-chat-quote-line' },
                              ].map((item) => (
                                <button
                                  key={item.value}
                                  onClick={() => {
                                    setDimensionContentType(item.value as any);
                                    setShowDimensionTypeDropdown(false);
                                  }}
                                  className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-3 ${
                                    dimensionContentType === item.value ? 'text-[#4A8AE9] bg-[#4A8AE9]/5' : 'text-gray-700'
                                  }`}
                                >
                                  <i className={`${item.icon} w-4 h-4 flex items-center justify-center`}></i>
                                  <span>{item.label}</span>
                                  {dimensionContentType === item.value && <i className="ri-check-line ml-auto text-[#4A8AE9]"></i>}
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>

                      {/* 时间筛选 */}
                      <div className="relative">
                        <button
                          onClick={() => {
                            setShowDimensionTimeDropdown(!showDimensionTimeDropdown);
                            setShowDimensionSentimentDropdown(false);
                            setShowDimensionPlatformDropdown(false);
                            setShowDimensionRoleDropdown(false);
                            setShowDimensionTypeDropdown(false);
                          }}
                          className={`flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap ${
                            dimensionContentStartDate && dimensionContentEndDate ? 'text-[#4A8AE9] border-[#4A8AE9]' : 'text-gray-700'
                          }`}
                        >
                          <i className={`ri-calendar-line w-4 h-4 flex items-center justify-center ${
                            dimensionContentStartDate && dimensionContentEndDate ? 'text-[#4A8AE9]' : 'text-gray-600'
                          }`}></i>
                          <span>{dimensionContentStartDate && dimensionContentEndDate ? '已选时间' : '时间'}</span>
                          <i className="ri-arrow-down-s-line text-gray-400"></i>
                        </button>
                        
                        {showDimensionTimeDropdown && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setShowDimensionTimeDropdown(false)}></div>
                            <div className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 p-4 z-20 w-72">
                              <div className="space-y-3">
                                <div>
                                  <label className="block text-sm font-medium text-gray-900 mb-1.5">开始日期</label>
                                  <input
                                    type="date"
                                    value={dimensionContentStartDate}
                                    onChange={(e) => setDimensionContentStartDate(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4A8AE9] focus:border-transparent"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-900 mb-1.5">结束日期</label>
                                  <input
                                    type="date"
                                    value={dimensionContentEndDate}
                                    onChange={(e) => setDimensionContentEndDate(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4A8AE9] focus:border-transparent"
                                  />
                                </div>
                                <div className="flex items-center gap-2 pt-2">
                                  <button
                                    onClick={() => setShowDimensionTimeDropdown(false)}
                                    className="flex-1 px-3 py-2 bg-[#4A8AE9] text-white rounded-lg text-sm font-medium hover:bg-[#3A7AD9] transition-all cursor-pointer whitespace-nowrap"
                                  >
                                    应用
                                  </button>
                                  <button
                                    onClick={() => {
                                      setDimensionContentStartDate('');
                                      setDimensionContentEndDate('');
                                      setShowDimensionTimeDropdown(false);
                                    }}
                                    className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-all cursor-pointer whitespace-nowrap"
                                  >
                                    重置
                                  </button>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>

                      {/* 清空筛选 */}
                      {(dimensionContentSentiment !== 'all' || dimensionContentPlatform !== 'all' || dimensionContentRole !== 'all' || dimensionContentType !== 'all' || (dimensionContentStartDate && dimensionContentEndDate)) && (
                        <button
                          onClick={() => {
                            setDimensionContentSentiment('all');
                            setDimensionContentPlatform('all');
                            setDimensionContentRole('all');
                            setDimensionContentType('all');
                            setDimensionContentStartDate('');
                            setDimensionContentEndDate('');
                          }}
                          className="text-sm text-[#4A8AE9] hover:text-[#3A7AD9] cursor-pointer whitespace-nowrap flex items-center gap-1"
                        >
                          <i className="ri-close-circle-line"></i>
                          清空筛选
                        </button>
                      )}
                    </div>
                    
                    {/* 内容列表 */}
                    <div className="space-y-4">
                      {getDimensionHighlightedContent(dimensionTab)
                        .filter(content => {
                          // 情感筛选
                          if (dimensionContentSentiment !== 'all') {
                            const hasMatchingSentiment = content.highlights.some(h => h.sentiment === dimensionContentSentiment);
                            if (!hasMatchingSentiment) return false;
                          }
                          // 平台筛选
                          if (dimensionContentPlatform !== 'all' && content.platform !== dimensionContentPlatform) return false;
                          // 角色筛选
                          if (dimensionContentRole !== 'all' && content.authorType !== dimensionContentRole) return false;
                          // 类型筛选
                          if (dimensionContentType !== 'all' && content.type !== dimensionContentType) return false;
                          // 时间筛选（简化处理，实际需要解析时间字符串）
                          return true;
                        })
                        .map((content) => (
                        <div 
                          key={content.id} 
                          className="rounded-xl p-5 border transition-all hover:shadow-md cursor-pointer bg-gray-50 border-gray-100 hover:border-gray-200"
                          onClick={() => {
                            const mockPost = mockContents.find(c => c.author === content.author) || mockContents[0];
                            openContentDetailModal(content.type, mockPost, content.type === 'comment' ? mockComments[0] : null);
                          }}
                        >
                          {/* 头部信息 */}
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-[#4A8AE9]/10">
                              <i className={`${
                                content.authorType === '观众' ? 'ri-user-line' :
                                content.authorType === 'KOL' ? 'ri-star-line' :
                                content.authorType === '官方' ? 'ri-shield-check-line' :
                                'ri-graduation-cap-line'
                              } text-[#4A8AE9] text-base`}></i>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-semibold text-gray-900">{content.author}</span>
                                <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                                  content.type === 'post' 
                                    ? 'bg-[#4A8AE9]/10 text-[#4A8AE9]' 
                                    : 'bg-amber-500/10 text-amber-700'
                                }`}>
                                  {content.type === 'post' ? '帖子' : '评论'}
                                </span>
                                <span className="px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded">
                                  {content.authorType}
                                </span>
                                <span className={`px-2 py-0.5 text-xs rounded whitespace-nowrap ${getPlatformBadgeStyle(content.platform)}`}>
                                  {content.platform}
                                </span>
                              </div>
                              <span className="text-xs text-gray-500 mt-1 block">{content.time}</span>
                            </div>
                          </div>

                          {/* 高亮文本内容 */}
                          <div className="text-sm leading-relaxed">
                            {renderHighlightedText(content.originalText, content.highlights)}
                          </div>
                        </div>
                      ))}
                      
                      {/* 空状态 */}
                      {getDimensionHighlightedContent(dimensionTab)
                        .filter(content => {
                          if (dimensionContentSentiment !== 'all') {
                            const hasMatchingSentiment = content.highlights.some(h => h.sentiment === dimensionContentSentiment);
                            if (!hasMatchingSentiment) return false;
                          }
                          if (dimensionContentPlatform !== 'all' && content.platform !== dimensionContentPlatform) return false;
                          if (dimensionContentRole !== 'all' && content.authorType !== dimensionContentRole) return false;
                          if (dimensionContentType !== 'all' && content.type !== dimensionContentType) return false;
                          return true;
                        }).length === 0 && (
                        <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-100">
                          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i className="ri-inbox-line text-gray-400 text-3xl"></i>
                          </div>
                          <p className="text-gray-500 text-sm">暂无符合条件的内容</p>
                        </div>
                      )}
                    </div>

                    {/* 查看更多按钮 */}
                    <div className="flex justify-center mt-6">
                      <button
                        onClick={() => setActiveTab('content')}
                        className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors cursor-pointer flex items-center gap-2"
                      >
                        查看全部原声内容
                        <i className="ri-arrow-right-line"></i>
                      </button>
                    </div>
                  </Card>
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'audience' && (
            <div className="space-y-6">
              {/* 平台筛选 */}
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 bg-white border border-gray-300 rounded-lg p-1 flex-shrink-0">
                  <button
                    onClick={() => setAudiencePlatformFilter('all')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex-shrink-0 flex items-center gap-1.5 ${
                      audiencePlatformFilter === 'all'
                        ? 'bg-[#4A8AE9] text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <i className="ri-apps-line text-base"></i>
                    <span>全部平台</span>
                  </button>
                  {selectedProject.platforms.map((platform, idx) => (
                    <button
                      key={idx}
                      onClick={() => setAudiencePlatformFilter(platform)}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex-shrink-0 flex items-center gap-1.5 ${
                        audiencePlatformFilter === platform
                          ? 'bg-[#4A8AE9] text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <i className={`${
                        platform === '小红书' ? 'ri-book-mark-line' :
                        platform === '微博' ? 'ri-weibo-line' :
                        platform === '抖音' ? 'ri-music-line' :
                        'ri-video-line'
                      } text-base`}></i>
                      <span>{platform}</span>
                    </button>
                  ))}
                </div>

                <div className="ml-auto text-sm text-gray-600">
                  共 {audienceData.reduce((sum, item) => sum + item.count, 0)} 条内容
                </div>
              </div>

              {/* 角色统计卡片 */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {audienceData.map((item, idx) => (
                  <Card key={idx}>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#4A8AE9]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className={`${
                          item.type === '观众' ? 'ri-user-line' :
                          item.type === 'KOL' ? 'ri-star-line' :
                          item.type === '官方' ? 'ri-shield-check-line' :
                          'ri-graduation-cap-line'
                        } text-[#4A8AE9] text-2xl`}></i>
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">{item.type}</h4>
                      <p className="text-3xl font-bold text-[#4A8AE9] mb-1">{item.count.toLocaleString()}</p>
                      <p className="text-sm text-gray-500 mb-3">{item.percentage}% 占比</p>
                      
                      {/* 情感分布 */}
                      <div className="pt-3 border-t border-gray-100 space-y-2">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-green-600 flex items-center gap-1">
                            <i className="ri-thumb-up-line"></i>
                            正面
                          </span>
                          <span className="font-semibold text-green-600">{item.positive}%</span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-600 flex items-center gap-1">
                            <i className="ri-chat-3-line"></i>
                            中性
                          </span>
                          <span className="font-semibold text-gray-600">{item.neutral}%</span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-red-600 flex items-center gap-1">
                            <i className="ri-thumb-down-line"></i>
                            负面
                          </span>
                          <span className="font-semibold text-red-600">{item.negative}%</span>
                        </div>
                      </div>

                      <div className="pt-3 border-t border-gray-100 mt-3">
                        <p className="text-xs text-gray-600">平均互动量</p>
                        <p className="text-lg font-semibold text-gray-900">{item.avgEngagement.toLocaleString()}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              {/* 角色词云 */}
              <Card>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">关键词云</h3>
                  <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                    {['观众', 'KOL', '官方', '专家'].map((role) => (
                      <button
                        key={role}
                        onClick={() => setSelectedAudienceRole(role)}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                          selectedAudienceRole === role
                            ? 'bg-[#4A8AE9] text-white'
                            : 'text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {role}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="relative h-96 bg-gradient-to-br from-gray-50 to-white rounded-lg p-8 flex items-center justify-center overflow-hidden">
                  <div className="relative w-full h-full flex flex-wrap items-center justify-center gap-4">
                    {getRoleWordCloud(selectedAudienceRole).map((word, idx) => (
                      <div
                        key={idx}
                        className="transition-all duration-300 hover:scale-110 cursor-pointer"
                        style={{
                          fontSize: `${word.size}px`,
                          color: word.color,
                          fontWeight: word.size > 35 ? 'bold' : word.size > 25 ? '600' : 'normal',
                          opacity: 0.8 + (word.size / 100),
                          animation: `float ${3 + idx * 0.5}s ease-in-out infinite`,
                          animationDelay: `${idx * 0.2}s`,
                        }}
                      >
                        {word.word}
                      </div>
                    ))}
                  </div>
                </div>
              </Card>

              <Card>
                <h3 className="text-lg font-semibold text-gray-900 mb-6">声量分布趋势</h3>
                <div className="space-y-6">
                  {/* 柱状图 */}
                  <div className="space-y-8">
                    <div className="flex items-end justify-center gap-12 h-64 px-4">
                      {rolePlatformData.roles.map((role, roleIdx) => {
                        const roleCounts = rolePlatformData.platforms.map(platform => 
                          rolePlatformData.distribution[role][platform]
                        );
                        const maxCount = Math.max(...Object.values(rolePlatformData.distribution).flatMap(p => Object.values(p)));
                        const totalCount = roleCounts.reduce((a, b) => a + b, 0);
                        
                        return (
                          <div key={roleIdx} className="flex flex-col items-center gap-3">
                            {/* 柱状图组 */}
                            <div className="flex items-end justify-center gap-1.5 h-48">
                              {rolePlatformData.platforms.map((platform, platformIdx) => {
                                const count = roleCounts[platformIdx];
                                const heightPercent = maxCount > 0 ? (count / maxCount) * 100 : 0;
                                
                                const platformColors: Record<string, { bg: string; hover: string }> = {
                                  '小红书': { bg: 'bg-[#FF2442]', hover: 'hover:bg-[#E01030]' },
                                  '微博': { bg: 'bg-[#E6A23C]', hover: 'hover:bg-[#D4912A]' },
                                  '抖音': { bg: 'bg-[#000000]', hover: 'hover:bg-[#1a1a1a]' },
                                  '视频号': { bg: 'bg-[#07C160]', hover: 'hover:bg-[#06AD51]' },
                                };
                                
                                const colors = platformColors[platform] || { bg: 'bg-[#4A8AE9]', hover: 'hover:bg-[#3A7AD9]' };
                                
                                return (
                                  <div 
                                    key={platformIdx} 
                                    className={`w-10 ${colors.bg} ${colors.hover} rounded-t transition-all cursor-pointer relative group min-h-[8px]`}
                                    style={{ height: `${heightPercent}%` }}
                                  >
                                    <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                      {platform}: {count} 条
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            
                            {/* 角色标签 */}
                            <div className="text-center">
                              <div className="flex items-center justify-center gap-2 mb-1">
                                <div className="w-6 h-6 bg-[#4A8AE9]/10 rounded flex items-center justify-center">
                                  <i className={`${
                                    role === '观众' ? 'ri-user-line' :
                                    role === 'KOL' ? 'ri-star-line' :
                                    role === '官方' ? 'ri-shield-check-line' :
                                    'ri-graduation-cap-line'
                                  } text-[#4A8AE9] text-sm`}></i>
                                </div>
                              </div>
                              <p className="text-sm font-medium text-gray-900">{role}</p>
                              <p className="text-xs text-gray-500 mt-0.5">共 {totalCount} 条</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* 图例 */}
                  <div className="flex items-center justify-center gap-6 pt-4">
                    {rolePlatformData.platforms.map((platform, idx) => {
                      const platformColors: Record<string, string> = {
                        '小红书': '#FF2442',
                        '微博': '#E6A23C',
                        '抖音': '#000000',
                        '视频号': '#07C160',
                      };
                      return (
                        <div key={idx} className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded"
                            style={{ backgroundColor: platformColors[platform] || '#4A8AE9' }}
                          ></div>
                          <span className="text-sm text-gray-600">{platform}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Card>

              {/* 人群洞察 */}
              <Card>
                <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                  <i className="ri-lightbulb-flash-line text-[#4A8AE9]"></i>
                  人群洞察
                  <span className="text-xs font-normal text-gray-400 ml-2">
                    基于各角色发布内容的智能分析
                  </span>
                </h3>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* 观众洞察 */}
                  <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-5 border border-blue-100">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-blue-500/10 rounded-full flex items-center justify-center">
                        <i className="ri-user-line text-blue-600 text-xl"></i>
                      </div>
                      <div>
                        <h4 className="text-base font-semibold text-gray-900">观众</h4>
                        <p className="text-xs text-gray-500">普通参观者的真实反馈</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="bg-white rounded-lg p-3 border border-blue-100">
                        <div className="flex items-center gap-2 mb-2">
                          <i className="ri-focus-3-line text-blue-600 text-sm"></i>
                          <span className="text-xs font-medium text-blue-700">核心关注点</span>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          观众最关注展览的<span className="text-blue-600 font-medium">视觉体验</span>和<span className="text-blue-600 font-medium">氛围营造</span>，对灯光设计、布景还原度评价较高，同时也关心参观便利性和票价性价比。
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <i className="ri-thumb-up-fill text-green-600 text-xs"></i>
                            <span className="text-xs font-medium text-green-700">正面评价</span>
                          </div>
                          <p className="text-xs text-gray-600 leading-relaxed">
                            沉浸感强、拍照出片、值得推荐、适合亲子
                          </p>
                        </div>
                        <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <i className="ri-thumb-down-fill text-red-600 text-xs"></i>
                            <span className="text-xs font-medium text-red-700">负面评价</span>
                          </div>
                          <p className="text-xs text-gray-600 leading-relaxed">
                            人多拥挤、排队时间长、部分区域太暗
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* KOL洞察 */}
                  <div className="bg-gradient-to-br from-amber-50 to-white rounded-xl p-5 border border-amber-100">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-amber-500/10 rounded-full flex items-center justify-center">
                        <i className="ri-star-line text-amber-600 text-xl"></i>
                      </div>
                      <div>
                        <h4 className="text-base font-semibold text-gray-900">KOL</h4>
                        <p className="text-xs text-gray-500">意见领袖的专业视角</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="bg-white rounded-lg p-3 border border-amber-100">
                        <div className="flex items-center gap-2 mb-2">
                          <i className="ri-focus-3-line text-amber-600 text-sm"></i>
                          <span className="text-xs font-medium text-amber-700">核心关注点</span>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          KOL更注重展览的<span className="text-amber-600 font-medium">专业性</span>和<span className="text-amber-600 font-medium">内容深度</span>，会从策展理念、设计水准、艺术价值等角度进行分析，内容更具传播性和影响力。
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <i className="ri-thumb-up-fill text-green-600 text-xs"></i>
                            <span className="text-xs font-medium text-green-700">正面评价</span>
                          </div>
                          <p className="text-xs text-gray-600 leading-relaxed">
                            策展专业、设计精良、内容深度、视觉震撼
                          </p>
                        </div>
                        <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <i className="ri-thumb-down-fill text-red-600 text-xs"></i>
                            <span className="text-xs font-medium text-red-700">负面评价</span>
                          </div>
                          <p className="text-xs text-gray-600 leading-relaxed">
                            部分复制品、互动体验少、动线设计待优化
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 官方洞察 */}
                  <div className="bg-gradient-to-br from-emerald-50 to-white rounded-xl p-5 border border-emerald-100">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-emerald-500/10 rounded-full flex items-center justify-center">
                        <i className="ri-shield-check-line text-emerald-600 text-xl"></i>
                      </div>
                      <div>
                        <h4 className="text-base font-semibold text-gray-900">官方</h4>
                        <p className="text-xs text-gray-500">主办方及合作机构发声</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="bg-white rounded-lg p-3 border border-emerald-100">
                        <div className="flex items-center gap-2 mb-2">
                          <i className="ri-focus-3-line text-emerald-600 text-sm"></i>
                          <span className="text-xs font-medium text-emerald-700">核心关注点</span>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          官方内容侧重于<span className="text-emerald-600 font-medium">文化传承</span>和<span className="text-emerald-600 font-medium">品牌宣传</span>，强调展览的历史意义、文化价值和社会影响力，内容权威性高但互动性相对较低。
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <i className="ri-thumb-up-fill text-green-600 text-xs"></i>
                            <span className="text-xs font-medium text-green-700">传播重点</span>
                          </div>
                          <p className="text-xs text-gray-600 leading-relaxed">
                            文化传承、艺术价值、历史意义、精品展出
                          </p>
                        </div>
                        <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <i className="ri-information-line text-amber-600 text-xs"></i>
                            <span className="text-xs font-medium text-amber-700">改进方向</span>
                          </div>
                          <p className="text-xs text-gray-600 leading-relaxed">
                            增加互动内容、提升用户参与、丰富活动形式
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 专家洞察 */}
                  <div className="bg-gradient-to-br from-violet-50 to-white rounded-xl p-5 border border-violet-100">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-violet-500/10 rounded-full flex items-center justify-center">
                        <i className="ri-graduation-cap-line text-violet-600 text-xl"></i>
                      </div>
                      <div>
                        <h4 className="text-base font-semibold text-gray-900">专家</h4>
                        <p className="text-xs text-gray-500">学术界的深度解读</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="bg-white rounded-lg p-3 border border-violet-100">
                        <div className="flex items-center gap-2 mb-2">
                          <i className="ri-focus-3-line text-violet-600 text-sm"></i>
                          <span className="text-xs font-medium text-violet-700">核心关注点</span>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          专家从<span className="text-violet-600 font-medium">学术研究</span>和<span className="text-violet-600 font-medium">历史考证</span>角度出发，关注展品的文物价值、历史背景和学术意义，评价更加客观专业，对教育功能有较高期待。
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <i className="ri-thumb-up-fill text-green-600 text-xs"></i>
                            <span className="text-xs font-medium text-green-700">正面评价</span>
                          </div>
                          <p className="text-xs text-gray-600 leading-relaxed">
                            学术价值高、内容编排合理、教育意义强
                          </p>
                        </div>
                        <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <i className="ri-thumb-down-fill text-red-600 text-xs"></i>
                            <span className="text-xs font-medium text-red-700">负面评价</span>
                          </div>
                          <p className="text-xs text-gray-600 leading-relaxed">
                            说明文字不足、缺少深度解读、互动体验少
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 洞察总结 */}
                <div className="mt-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-[#4A8AE9]/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-robot-line text-[#4A8AE9]"></i>
                    </div>
                    <div>
                      <h5 className="text-sm font-semibold text-gray-900 mb-2">AI 综合洞察</h5>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        不同角色对展览的关注点存在明显差异：<span className="font-medium text-gray-900">观众</span>更关注体验感受和实用性，<span className="font-medium text-gray-900">KOL</span>侧重专业性和传播价值，<span className="font-medium text-gray-900">官方</span>强调文化意义和品牌形象，<span className="font-medium text-gray-900">专家</span>则聚焦学术价值和教育功能。建议针对不同人群制定差异化的内容策略和服务优化方案。
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}
          
          {activeTab === 'content' && (
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                {/* 情感筛选 */}
                <div className="relative">
                  <button
                    onClick={() => {
                      setShowSentimentDropdown(!showSentimentDropdown);
                      setShowPlatformDropdown(false);
                      setShowRoleDropdown(false);
                      setShowTimeDropdown(false);
                    }}
                    className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className={`${
                      selectedSentiment === 'positive' ? 'ri-thumb-up-line text-green-600' :
                      selectedSentiment === 'neutral' ? 'ri-chat-3-line text-gray-600' :
                      selectedSentiment === 'negative' ? 'ri-thumb-down-line text-red-600' :
                      'ri-emotion-line text-gray-600'
                    } w-4 h-4 flex items-center justify-center`}></i>
                    <span>
                      {selectedSentiment === 'all' ? '情感' : 
                       selectedSentiment === 'positive' ? '正面' :
                       selectedSentiment === 'neutral' ? '中性' : '负面'}
                    </span>
                    <i className="ri-arrow-down-s-line text-gray-400"></i>
                  </button>
                  
                  {showSentimentDropdown && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowSentimentDropdown(false)}></div>
                      <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-20">
                        <button
                          onClick={() => {
                            setSelectedSentiment('all');
                            setShowSentimentDropdown(false);
                          }}
                          className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-3 ${
                            selectedSentiment === 'all' ? 'text-[#4A8AE9] bg-[#4A8AE9]/5' : 'text-gray-700'
                          }`}
                        >
                          <i className="ri-emotion-line w-4 h-4 flex items-center justify-center"></i>
                          <span>全部情感</span>
                          {selectedSentiment === 'all' && <i className="ri-check-line ml-auto text-[#4A8AE9]"></i>}
                        </button>
                        <button
                          onClick={() => {
                            setSelectedSentiment('positive');
                            setShowSentimentDropdown(false);
                          }}
                          className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-3 ${
                            selectedSentiment === 'positive' ? 'text-green-600 bg-green-50' : 'text-gray-700'
                          }`}
                        >
                          <i className="ri-thumb-up-line w-4 h-4 flex items-center justify-center"></i>
                          <span>正面</span>
                          {selectedSentiment === 'positive' && <i className="ri-check-line ml-auto text-green-600"></i>}
                        </button>
                        <button
                          onClick={() => {
                            setSelectedSentiment('neutral');
                            setShowSentimentDropdown(false);
                          }}
                          className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-3 ${
                            selectedSentiment === 'neutral' ? 'text-gray-600 bg-gray-50' : 'text-gray-700'
                          }`}
                        >
                          <i className="ri-chat-3-line w-4 h-4 flex items-center justify-center"></i>
                          <span>中性</span>
                          {selectedSentiment === 'neutral' && <i className="ri-check-line ml-auto text-gray-600"></i>}
                        </button>
                        <button
                          onClick={() => {
                            setSelectedSentiment('negative');
                            setShowSentimentDropdown(false);
                          }}
                          className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-3 ${
                            selectedSentiment === 'negative' ? 'text-red-600 bg-red-50' : 'text-gray-700'
                          }`}
                        >
                          <i className="ri-thumb-down-line w-4 h-4 flex items-center justify-center"></i>
                          <span>负面</span>
                          {selectedSentiment === 'negative' && <i className="ri-check-line ml-auto text-red-600"></i>}
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {/* 平台筛选 */}
                <div className="relative">
                  <button
                    onClick={() => {
                      setShowPlatformDropdown(!showPlatformDropdown);
                      setShowSentimentDropdown(false);
                      setShowRoleDropdown(false);
                      setShowTimeDropdown(false);
                    }}
                    className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-apps-line w-4 h-4 flex items-center justify-center text-gray-600"></i>
                    <span>{contentPlatformFilter === 'all' ? '平台' : contentPlatformFilter}</span>
                    <i className="ri-arrow-down-s-line text-gray-400"></i>
                  </button>
                  
                  {showPlatformDropdown && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowPlatformDropdown(false)}></div>
                      <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-20">
                        <button
                          onClick={() => {
                            setContentPlatformFilter('all');
                            setShowPlatformDropdown(false);
                          }}
                          className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-3 ${
                            contentPlatformFilter === 'all' ? 'text-[#4A8AE9] bg-[#4A8AE9]/5' : 'text-gray-700'
                          }`}
                        >
                          <i className="ri-apps-line w-4 h-4 flex items-center justify-center"></i>
                          <span>全部平台</span>
                          {contentPlatformFilter === 'all' && <i className="ri-check-line ml-auto text-[#4A8AE9]"></i>}
                        </button>
                        {selectedProject.platforms.map((platform, idx) => (
                          <button
                            key={idx}
                            onClick={() => {
                              setContentPlatformFilter(platform);
                              setShowPlatformDropdown(false);
                            }}
                            className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-3 ${
                              contentPlatformFilter === platform ? 'text-[#4A8AE9] bg-[#4A8AE9]/5' : 'text-gray-700'
                            }`}
                          >
                            <i className={`${
                              platform === '小红书' ? 'ri-book-mark-line' :
                              platform === '微博' ? 'ri-weibo-line' :
                              platform === '抖音' ? 'ri-music-line' :
                              'ri-video-line'
                            } w-4 h-4 flex items-center justify-center`}></i>
                            <span>{platform}</span>
                            {contentPlatformFilter === platform && <i className="ri-check-line ml-auto text-[#4A8AE9]"></i>}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* 角色筛选 */}
                <div className="relative">
                  <button
                    onClick={() => {
                      setShowRoleDropdown(!showRoleDropdown);
                      setShowSentimentDropdown(false);
                      setShowPlatformDropdown(false);
                      setShowTimeDropdown(false);
                    }}
                    className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-user-line w-4 h-4 flex items-center justify-center text-gray-600"></i>
                    <span>
                      {selectedRoles.length === 0 ? '角色' : 
                       selectedRoles.length === 1 ? selectedRoles[0] :
                       `${selectedRoles.length}个角色`}
                    </span>
                    <i className="ri-arrow-down-s-line text-gray-400"></i>
                  </button>
                  
                  {showRoleDropdown && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowRoleDropdown(false)}></div>
                      <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-20">
                        <button
                          onClick={() => {
                            setSelectedRoles([]);
                            setShowRoleDropdown(false);
                          }}
                          className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-3 ${
                            selectedRoles.length === 0 ? 'text-[#4A8AE9] bg-[#4A8AE9]/5' : 'text-gray-700'
                          }`}
                        >
                          <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                          <span>全部角色</span>
                          {selectedRoles.length === 0 && <i className="ri-check-line ml-auto text-[#4A8AE9]"></i>}
                        </button>
                        <div className="border-t border-gray-100 my-1"></div>
                        {['官方', '观众', '专家', 'KOL'].map((role, idx) => (
                          <button
                            key={idx}
                            onClick={() => toggleRole(role)}
                            className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-3 ${
                              selectedRoles.includes(role) ? 'text-[#4A8AE9] bg-[#4A8AE9]/5' : 'text-gray-700'
                            }`}
                          >
                            <i className={`${
                              role === '官方' ? 'ri-shield-check-line' :
                              role === '观众' ? 'ri-user-line' :
                              role === '专家' ? 'ri-graduation-cap-line' :
                              'ri-star-line'
                            } w-4 h-4 flex items-center justify-center`}></i>
                            <span>{role}</span>
                            {selectedRoles.includes(role) && <i className="ri-check-line ml-auto text-[#4A8AE9]"></i>}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* 时间筛选 */}
                <div className="relative">
                  <button
                    onClick={() => {
                      setShowTimeDropdown(!showTimeDropdown);
                      setShowSentimentDropdown(false);
                      setShowPlatformDropdown(false);
                      setShowRoleDropdown(false);
                    }}
                    className={`flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap ${
                      customStartDate && customEndDate ? 'text-[#4A8AE9] border-[#4A8AE9]' : 'text-gray-700'
                    }`}
                  >
                    <i className={`ri-calendar-line w-4 h-4 flex items-center justify-center ${
                      customStartDate && customEndDate ? 'text-[#4A8AE9]' : 'text-gray-600'
                    }`}></i>
                    <span>{customStartDate && customEndDate ? '已选时间' : '时间'}</span>
                    <i className="ri-arrow-down-s-line text-gray-400"></i>
                  </button>
                  
                  {showTimeDropdown && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowTimeDropdown(false)}></div>
                      <div className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 p-5 z-20 w-80">
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-semibold text-gray-900 mb-2">开始日期</label>
                            <input
                              type="date"
                              value={customStartDate}
                              onChange={(e) => setCustomStartDate(e.target.value)}
                              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4A8AE9] focus:border-transparent transition-all"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-900 mb-2">结束日期</label>
                            <input
                              type="date"
                              value={customEndDate}
                              onChange={(e) => setCustomEndDate(e.target.value)}
                              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4A8AE9] focus:border-transparent transition-all"
                            />
                          </div>
                          <div className="flex items-center gap-2 pt-2">
                            <button
                              onClick={() => {
                                applyCustomDateFilter();
                                setShowTimeDropdown(false);
                              }}
                              className="flex-1 px-4 py-2.5 bg-[#4A8AE9] text-white rounded-lg text-sm font-medium hover:bg-[#3A7AD9] transition-all shadow-sm cursor-pointer whitespace-nowrap"
                            >
                              应用
                            </button>
                            <button
                              onClick={() => {
                                resetCustomDateFilter();
                                setShowTimeDropdown(false);
                              }}
                              className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-all cursor-pointer whitespace-nowrap"
                            >
                              重置
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* 筛选结果统计 */}
                <div className="ml-auto flex items-center gap-2 text-sm text-gray-600">
                  <span>共 {filteredContents.length} 条内容</span>
                  {(selectedSentiment !== 'all' || contentPlatformFilter !== 'all' || selectedRoles.length > 0 || (customStartDate && customEndDate)) && (
                    <button
                      onClick={() => {
                        setSelectedSentiment('all');
                        setContentPlatformFilter('all');
                        setSelectedRoles([]);
                        setCustomStartDate('');
                        setCustomEndDate('');
                      }}
                      className="text-[#4A8AE9] hover:text-[#3A7AD9] cursor-pointer whitespace-nowrap flex items-center gap-1"
                    >
                      <i className="ri-close-circle-line"></i>
                      清空筛选
                    </button>
                  )}
                </div>
              </div>
              
              <div className="space-y-4">
                {filteredContents.length > 0 ? (
                  filteredContents.map((content) => (
                    <Card key={content.id}>
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-[#4A8AE9]/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-user-line text-[#4A8AE9] text-xl"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm font-medium text-gray-900">{content.author}</span>
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded whitespace-nowrap">
                              {content.authorType}
                            </span>
                            <span className="text-xs text-gray-500">·</span>
                            <span className={`px-2 py-0.5 text-xs rounded whitespace-nowrap ${getPlatformBadgeStyle(content.platform)}`}>
                              {content.platform}
                            </span>
                            <span className="text-xs text-gray-500">·</span>
                            <span className="text-xs text-gray-500">{content.publishTime}</span>
                          </div>

                          <p className="text-sm text-gray-700 mb-3 leading-relaxed">{content.content}</p>
                          {content.images && content.images.length > 0 && (
                            <div className="grid grid-cols-3 gap-2 mb-3">
                              {content.images.map((img, idx) => (
                                <div key={idx} className="w-full h-32 rounded-lg overflow-hidden bg-gray-100">
                                  <img src={img} alt="" className="w-full h-full object-cover object-top" />
                                </div>
                              ))}
                            </div>
                          )}
                          {content.video && (
                            <>
                              <div className="w-full h-48 rounded-lg overflow-hidden bg-gray-100 mb-3 relative group cursor-pointer">
                                <img src={content.video} alt="" className="w-full h-full object-cover object-top" />
                                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                  <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center group-hover:bg-white transition-colors">
                                    <i className="ri-play-fill text-[#4A8AE9] text-3xl ml-1"></i>
                                  </div>
                                </div>
                              </div>
                              
                              {/* 视频字幕识别内容 */}
                              <div className="mb-3 border border-gray-200 rounded-lg overflow-hidden">
                                <button
                                  onClick={() => toggleTranscript(content.id)}
                                  className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between cursor-pointer"
                                >
                                  <div className="flex items-center gap-2">
                                    <i className="ri-file-text-line text-[#4A8AE9] w-4 h-4 flex items-center justify-center"></i>
                                    <span className="text-sm font-medium text-gray-900">视频字幕识别</span>
                                  </div>
                                  <i className={`ri-arrow-${expandedTranscripts.includes(content.id) ? 'up' : 'down'}-s-line text-gray-500`}></i>
                                </button>
                                {expandedTranscripts.includes(content.id) && (
                                  <div className="p-4 bg-white border-t border-gray-200">
                                    <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
                                      <p>
                                        大家好，今天带大家参观敦煌艺术特展。这次展览的灯光设计真的非常用心，每一幅壁画都被照得栩栩如生。
                                      </p>
                                      <p>
                                        布景还原了洞窟的感觉，氛围感特别强。强烈推荐大家来现场感受一下。
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </>
                          )}

                          {/* 互动数据 - 根据平台显示不同数据 */}
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            {content.platform === '微博' && (
                              <>
                                <span className="flex items-center gap-1.5">
                                  <i className="ri-thumb-up-line"></i>
                                  {content.likes}&nbsp;
                                </span>
                                <span 
                                  className="flex items-center gap-1.5 hover:text-[#4A8AE9] cursor-pointer transition-colors"
                                  onClick={() => openCommentsModal(content.id)}
                                >
                                  <i className="ri-chat-3-line"></i>
                                  {content.comments}&nbsp;
                                </span>
                                <span className="flex items-center gap-1.5">
                                  <i className="ri-share-line"></i>
                                  {content.shares || 0}&nbsp;
                                </span>
                              </>
                            )}
                            {content.platform === '小红书' && (
                              <>
                                <span className="flex items-center gap-1.5">
                                  <i className="ri-heart-line"></i>
                                  {content.likes}&nbsp;
                                </span>
                                <span 
                                  className="flex items-center gap-1.5 hover:text-[#4A8AE9] cursor-pointer transition-colors"
                                  onClick={() => openCommentsModal(content.id)}
                                >
                                  <i className="ri-chat-3-line"></i>
                                  {content.comments}&nbsp;
                                </span>
                                <span className="flex items-center gap-1.5">
                                  <i className="ri-bookmark-line"></i>
                                  {content.collects || 0}&nbsp;
                                </span>
                              </>
                            )}
                            {content.platform === '抖音' && (
                              <>
                                <span className="flex items-center gap-1.5">
                                  <i className="ri-heart-line"></i>
                                  {content.likes}&nbsp;
                                </span>
                                <span 
                                  className="flex items-center gap-1.5 hover:text-[#4A8AE9] cursor-pointer transition-colors"
                                  onClick={() => openCommentsModal(content.id)}
                                >
                                  <i className="ri-chat-3-line"></i>
                                  {content.comments}&nbsp;
                                </span>
                                <span className="flex items-center gap-1.5">
                                  <i className="ri-bookmark-line"></i>
                                  {content.collects || 0}&nbsp;
                                </span>
                                <span className="flex items-center gap-1.5">
                                  <i className="ri-share-line"></i>
                                  {content.shares || 0}&nbsp;
                                </span>
                              </>
                            )}
                            {content.platform === '视频号' && (
                              <>
                                <span className="flex items-center gap-1.5">
                                  <i className="ri-thumb-up-line"></i>
                                  {content.likes}&nbsp;
                                </span>
                                <span 
                                  className="flex items-center gap-1.5 hover:text-[#4A8AE9] cursor-pointer transition-colors"
                                  onClick={() => openCommentsModal(content.id)}
                                >
                                  <i className="ri-chat-3-line"></i>
                                  {content.comments}&nbsp;
                                </span>
                                <span className="flex items-center gap-1.5">
                                  <i className="ri-heart-line"></i>
                                  {content.favorites || 0}&nbsp;
                                </span>
                                <span className="flex items-center gap-1.5">
                                  <i className="ri-share-line"></i>
                                  {content.shares || 0}&nbsp;
                                </span>
                              </>
                            )}
                          </div>

                          {/* AI 分数指标 */}
                          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100">
                            <div 
                              className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 rounded-lg px-2 py-1 -mx-2 -my-1 transition-colors group"
                              onClick={() => openRelevanceModal(content.id, 'content', content.relevanceScore >= 70)}
                              title="点击标记相关性"
                            >
                              <span className="text-xs text-gray-500 group-hover:text-[#4A8AE9]">相关性</span>
                              <div className="flex items-center gap-1">
                                <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-[#4A8AE9] rounded-full"
                                    style={{ width: `${content.relevanceScore}%` }}
                                  ></div>
                                </div>
                                <span className="text-xs font-medium text-[#4A8AE9]">{content.relevanceScore}</span>
                              </div>
                              <i className="ri-edit-line text-xs text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"></i>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">情感</span>
                              <div className="flex items-center gap-1">
                                <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full rounded-full ${
                                      content.sentimentScore >= 70 ? 'bg-green-500' :
                                      content.sentimentScore >= 40 ? 'bg-gray-400' : 'bg-red-500'
                                    }`}
                                    style={{ width: `${content.sentimentScore}%` }}
                                  ></div>
                                </div>
                                <span className={`text-xs font-medium ${
                                  content.sentimentScore >= 70 ? 'text-green-600' :
                                  content.sentimentScore >= 40 ? 'text-gray-600' : 'text-red-600'
                                }`}>{content.sentimentScore}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">影响力</span>
                              <div className="flex items-center gap-1">
                                <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-amber-500 rounded-full"
                                    style={{ width: `${content.influenceScore}%` }}
                                  ></div>
                                </div>
                                <span className="text-xs font-medium text-amber-600">{content.influenceScore}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                          content.sentiment === 'positive' ? 'bg-green-100 text-green-700' :
                          content.sentiment === 'neutral' ? 'bg-gray-100 text-gray-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {content.sentiment === 'positive' ? '正面' : content.sentiment === 'neutral' ? '中性' : '负面'}
                        </div>
                      </div>
                    </Card>
                  ))
                ) : (
                  <Card>
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-inbox-line text-gray-400 text-3xl"></i>
                      </div>
                      <p className="text-gray-500 text-sm">暂无符合条件的内容</p>
                    </div>
                  </Card>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* 项目配置弹窗 */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowConfigModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">项目配置信息</h3>
              <button
                onClick={() => setShowConfigModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-gray-500 text-xl"></i>
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-140px)]">
              <div className="space-y-6">
                {/* 项目名称 */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-[#4A8AE9]/10 rounded-lg flex items-center justify-center">
                      <i className="ri-file-text-line text-[#4A8AE9] text-base"></i>
                    </div>
                    <label className="text-sm font-semibold text-gray-900">项目名称</label>
                  </div>
                  <div className="px-4 py-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-900">{selectedProject.name}</p>
                  </div>
                </div>

                {/* 监测时间 */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-[#4A8AE9]/10 rounded-lg flex items-center justify-center">
                      <i className="ri-calendar-line text-[#4A8AE9] text-base"></i>
                    </div>
                    <label className="text-sm font-semibold text-gray-900">监测时间</label>
                  </div>
                  <div className="px-4 py-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-900">
                      {selectedProject.startDate} 至 {selectedProject.endDate}
                    </p>
                  </div>
                </div>

                {/* 项目状态 */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-[#4A8AE9]/10 rounded-lg flex items-center justify-center">
                      <i className="ri-pulse-line text-[#4A8AE9] text-base"></i>
                    </div>
                    <label className="text-sm font-semibold text-gray-900">项目状态</label>
                  </div>
                  <div className="px-4 py-3 bg-gray-50 rounded-lg">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      selectedProject.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedProject.status === 'active' ? '监测中' : '已完成'}
                    </span>
                  </div>
                </div>
                
                {/* 监测关键词 */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-[#4A8AE9]/10 rounded-lg flex items-center justify-center">
                      <i className="ri-key-2-line text-[#4A8AE9] text-base"></i>
                    </div>
                    <label className="text-sm font-semibold text-gray-900">监测关键词</label>
                  </div>
                  <div className="flex flex-wrap gap-2 p-4 bg-gray-50 rounded-lg">
                    {selectedProject.keywords.map((keyword, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900"
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
                
                {/* 监测平台 */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-[#4A8AE9]/10 rounded-lg flex items-center justify-center">
                      <i className="ri-apps-line text-[#4A8AE9] text-base"></i>
                    </div>
                    <label className="text-sm font-semibold text-gray-900">监测平台</label>
                  </div>
                  <div className="flex flex-wrap gap-2 p-4 bg-gray-50 rounded-lg">
                    {selectedProject.platforms.map((platform, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 flex items-center gap-2"
                      >
                        <i className={`${
                          platform === '小红书' ? 'ri-book-mark-line' :
                          platform === '微博' ? 'ri-weibo-line' :
                          platform === '抖音' ? 'ri-music-line' :
                          'ri-video-line'
                        } w-4 h-4 flex items-center justify-center`}></i>
                        {platform}
                      </span>
                    ))}
                  </div>
                </div>

                {/* 创建时间 */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-[#4A8AE9]/10 rounded-lg flex items-center justify-center">
                      <i className="ri-time-line text-[#4A8AE9] text-base"></i>
                    </div>
                    <label className="text-sm font-semibold text-gray-900">创建时间</label>
                  </div>
                  <div className="px-4 py-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-900">{selectedProject.createdAt}</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-end p-6 border-t border-gray-200 bg-gray-50">
              <Button onClick={() => setShowConfigModal(false)}>
                关闭
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 评论弹窗 */}
      {showCommentsModal && selectedContentForComments && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={closeCommentsModal}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#4A8AE9]/10 rounded-full flex items-center justify-center">
                  <i className="ri-chat-3-line text-[#4A8AE9] text-lg"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">评论详情</h3>
                  <p className="text-sm text-gray-500">共 {mockComments.length} 条评论</p>
                </div>
              </div>
              <button
                onClick={closeCommentsModal}
                className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-gray-500 text-xl"></i>
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              <div className="space-y-4">
                {mockComments.map((comment, idx) => (
                  <div key={idx} className="flex gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="w-10 h-10 bg-[#4A8AE9]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-user-line text-[#4A8AE9]"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-gray-900">{comment.user}</span>
                        <span className="text-xs text-gray-400">{comment.time}</span>
                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium whitespace-nowrap ${getSentimentBadgeStyle(comment.sentiment)}`}>
                          {getSentimentText(comment.sentiment)}
                        </span>
                      </div>

                      <p className="text-sm text-gray-700 mb-3 leading-relaxed">{comment.text}</p>
                      
                      <div className="flex items-center gap-4 mb-3">
                        <button className="flex items-center gap-1 text-xs text-gray-500 hover:text-[#4A8AE9] cursor-pointer transition-colors">
                          <i className="ri-heart-line"></i>
                          {comment.likes}
                        </button>
                      </div>

                      {/* AI 分数指标 */}
                      <div className="flex items-center gap-4 pt-3 border-t border-gray-200">
                        <div 
                          className="flex items-center gap-2 cursor-pointer hover:bg-white rounded-lg px-2 py-1 -mx-2 -my-1 transition-colors group"
                          onClick={(e) => {
                            e.stopPropagation();
                            openRelevanceModal(`comment-${idx}`, 'comment', comment.relevanceScore >= 70);
                          }}
                          title="点击标记相关性"
                        >
                          <span className="text-xs text-gray-500 group-hover:text-[#4A8AE9]">相关性</span>
                          <div className="flex items-center gap-1">
                            <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-[#4A8AE9] rounded-full"
                                style={{ width: `${comment.relevanceScore}%` }}
                              ></div>
                            </div>
                            <span className="text-xs font-medium text-[#4A8AE9]">{comment.relevanceScore}</span>
                          </div>
                          <i className="ri-edit-line text-xs text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"></i>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">情感</span>
                          <div className="flex items-center gap-1">
                            <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full ${
                                  comment.sentimentScore >= 70 ? 'bg-green-500' :
                                  comment.sentimentScore >= 40 ? 'bg-gray-400' : 'bg-red-500'
                                }`}
                                style={{ width: `${comment.sentimentScore}%` }}
                              ></div>
                            </div>
                            <span className={`text-xs font-medium ${
                              comment.sentimentScore >= 70 ? 'text-green-600' :
                              comment.sentimentScore >= 40 ? 'text-gray-600' : 'text-red-600'
                            }`}>{comment.sentimentScore}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">影响力</span>
                          <div className="flex items-center gap-1">
                            <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-amber-500 rounded-full"
                                style={{ width: `${comment.influenceScore}%` }}
                              ></div>
                            </div>
                            <span className="text-xs font-medium text-amber-600">{comment.influenceScore}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  <span className="text-gray-600">正面: {mockComments.filter(c => c.sentiment === 'positive').length}</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                  <span className="text-gray-600">中性: {mockComments.filter(c => c.sentiment === 'neutral').length}</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                  <span className="text-gray-600">负面: {mockComments.filter(c => c.sentiment === 'negative').length}</span>
                </span>
              </div>
              <Button onClick={closeCommentsModal}>
                关闭
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 相关性标记弹窗 */}
      {showRelevanceModal && selectedItemForRelevance && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={closeRelevanceModal}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#4A8AE9]/10 rounded-full flex items-center justify-center">
                  <i className="ri-flag-line text-[#4A8AE9] text-lg"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">标记相关性</h3>
                  <p className="text-sm text-gray-500">人工修正AI判断</p>
                </div>
              </div>
              <button
                onClick={closeRelevanceModal}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-gray-500 text-xl"></i>
              </button>
            </div>
            
            <div className="p-6 space-y-5">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-information-line text-[#4A8AE9]"></i>
                  <span className="text-sm font-medium text-gray-900">当前AI判断</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    selectedItemForRelevance.currentRelevance 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {selectedItemForRelevance.currentRelevance ? '相关' : '不相关'}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  备注说明 <span className="text-gray-400 font-normal">(可选)</span>
                </label>
                <textarea
                  value={relevanceNote}
                  onChange={(e) => setRelevanceNote(e.target.value)}
                  placeholder="请说明标记原因，帮助改进AI判断..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#4A8AE9] focus:border-transparent resize-none"
                  rows={3}
                  maxLength={200}
                />
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-gray-500">最多200字</span>
                  <span className="text-xs text-gray-400">{relevanceNote.length}/200</span>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={() => submitRelevanceMark(true)}
                  className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-check-line"></i>
                  标记为相关
                </button>
                <button
                  onClick={() => submitRelevanceMark(false)}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-close-line"></i>
                  标记为不相关
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 原声内容详情弹窗 */}
      {showContentDetailModal && selectedContentDetail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={closeContentDetailModal}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* 弹窗头部 */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#4A8AE9]/10 rounded-full flex items-center justify-center">
                  <i className={`${selectedContentDetail.type === 'post' ? 'ri-article-line' : 'ri-chat-quote-line'} text-[#4A8AE9] text-lg`}></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedContentDetail.type === 'post' ? '帖子详情' : '评论来源'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    查看原帖及评论
                  </p>
                </div>
              </div>
            </div>
            
            {/* 弹窗内容 */}
            <div className="p-6 overflow-y-auto flex-1">
              {/* 原帖子内容 */}
              {selectedContentDetail.post && (
                <div className="mb-6">
                  <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                    {/* 作者信息 */}
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-[#4A8AE9]/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className={`${
                          selectedContentDetail.post.authorType === '观众' ? 'ri-user-line' :
                          selectedContentDetail.post.authorType === 'KOL' ? 'ri-star-line' :
                          selectedContentDetail.post.authorType === '官方' ? 'ri-shield-check-line' :
                          'ri-graduation-cap-line'
                        } text-[#4A8AE9] text-lg`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-gray-900">{selectedContentDetail.post.author}</span>
                          <span className="px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded whitespace-nowrap">
                            {selectedContentDetail.post.authorType}
                          </span>
                          <span className={`px-2 py-0.5 text-xs rounded whitespace-nowrap ${getPlatformBadgeStyle(selectedContentDetail.post.platform)}`}>
                            {selectedContentDetail.post.platform}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-500">{selectedContentDetail.post.publishTime}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                            selectedContentDetail.post.sentiment === 'positive' ? 'bg-green-100 text-green-700' :
                            selectedContentDetail.post.sentiment === 'neutral' ? 'bg-gray-200 text-gray-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {selectedContentDetail.post.sentiment === 'positive' ? '正面' : selectedContentDetail.post.sentiment === 'neutral' ? '中性' : '负面'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* 帖子内容 */}
                    <p className="text-sm text-gray-700 leading-relaxed mb-4">
                      {selectedContentDetail.post.content}
                    </p>

                    {/* 图片展示 */}
                    {selectedContentDetail.post.images && selectedContentDetail.post.images.length > 0 && (
                      <div className={`grid gap-2 mb-4 ${
                        selectedContentDetail.post.images.length === 1 ? 'grid-cols-1' :
                        selectedContentDetail.post.images.length === 2 ? 'grid-cols-2' :
                        'grid-cols-3'
                      }`}>
                        {selectedContentDetail.post.images.map((img, imgIdx) => (
                          <div key={imgIdx} className="aspect-square rounded-lg overflow-hidden bg-gray-200">
                            <img src={img} alt="" className="w-full h-full object-cover object-top" />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* 视频缩略图 */}
                    {selectedContentDetail.post.video && !selectedContentDetail.post.images && (
                      <div className="relative rounded-lg overflow-hidden bg-gray-200 mb-4 aspect-video">
                        <img src={selectedContentDetail.post.video} alt="" className="w-full h-full object-cover object-top" />
                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                          <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center">
                            <i className="ri-play-fill text-[#4A8AE9] text-2xl ml-0.5"></i>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* 互动数据 */}
                    <div className="flex items-center gap-4 pt-3 border-t border-gray-200">
                      <span className="flex items-center gap-1.5 text-sm text-gray-500">
                        <i className="ri-heart-line"></i>
                        {selectedContentDetail.post.likes}
                      </span>
                      <span className="flex items-center gap-1.5 text-sm text-gray-500">
                        <i className="ri-chat-3-line"></i>
                        {selectedContentDetail.post.comments}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* 评论列表 */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <span className="px-2.5 py-1 bg-amber-500/10 text-amber-700 text-xs rounded-lg font-medium">
                    全部评论
                  </span>
                  <span className="text-sm text-gray-500">共 {mockComments.length} 条</span>
                </div>
                
                <div className="space-y-3">
                  {mockComments.map((comment, idx) => (
                    <div 
                      key={idx} 
                      className={`p-4 rounded-xl border transition-colors ${
                        selectedContentDetail.type === 'comment' && selectedContentDetail.comment?.user === comment.user && selectedContentDetail.comment?.text === comment.text
                          ? 'bg-amber-50 border-amber-300 ring-2 ring-amber-200'
                          : 'bg-gray-50 border-gray-100 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 bg-amber-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-user-line text-amber-600 text-sm"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-900">{comment.user}</span>
                            <span className="text-xs text-gray-400">{comment.time}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                              comment.sentiment === 'positive' ? 'bg-green-100 text-green-700' :
                              comment.sentiment === 'neutral' ? 'bg-gray-200 text-gray-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {comment.sentiment === 'positive' ? '正面' : comment.sentiment === 'neutral' ? '中性' : '负面'}
                            </span>
                            {selectedContentDetail.type === 'comment' && selectedContentDetail.comment?.user === comment.user && selectedContentDetail.comment?.text === comment.text && (
                              <span className="px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full font-medium">
                                当前查看
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 leading-relaxed">{comment.text}</p>
                          <div className="flex items-center gap-1.5 mt-2 text-xs text-gray-400">
                            <i className="ri-heart-line"></i>
                            <span>{comment.likes}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* 弹窗底部 */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  <span className="text-gray-600">正面: {mockComments.filter(c => c.sentiment === 'positive').length}</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                  <span className="text-gray-600">中性: {mockComments.filter(c => c.sentiment === 'neutral').length}</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                  <span className="text-gray-600">负面: {mockComments.filter(c => c.sentiment === 'negative').length}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 成功提示 Toast */}
      {showSuccessToast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[100] animate-fade-in">
          <div className="flex items-center gap-2 px-5 py-3 bg-green-600 text-white rounded-lg shadow-lg">
            <i className="ri-check-circle-line text-lg"></i>
            <span className="text-sm font-medium">{successToastMessage}</span>
          </div>
        </div>
      )}
    </div>
  );
}
